import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Plus, Search, Filter, Package, Car, Cog, Building2, Archive as ArchiveIcon,
  Download, MapPin, User as UserIcon, Pencil, X, Check, FileText, History,
  Camera, ClipboardCheck, Wrench, ShieldAlert, ArrowRightLeft, Palette,
  Upload, Trash2, QrCode, Printer, FileSpreadsheet, Layers,
  Columns3,
} from "lucide-react";
import { ASSET_COLUMN_DEFS, ASSET_DEFAULT_COLUMNS, ASSET_COL_LABEL, assetCellValue } from "@/lib/assetColumns";
import { QRCodeSVG } from "qrcode.react";
import { toast } from "sonner";
import api, { formatDetail } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useAccess } from "@/lib/access";
import { fmtDate } from "@/lib/constants";
import { exportAssetsToExcel, printAssetsToPDF, printAssetCardsBulk } from "@/lib/assetExport";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import DateField from "@/components/DateField";
import ConfirmDialog from "@/components/ConfirmDialog";
import { LocationSelect, LocationCombo, resolveLocationId, resolveOrCreateLocationByName, useLocationOptions } from "@/components/assets/LocationSelect";

const CATEGORIES = [
  { v: "office", label: "Office", icon: Package, tone: "sky" },
  { v: "vehicle", label: "Kendaraan Bermotor", icon: Car, tone: "orange" },
  { v: "machine", label: "Mesin", icon: Cog, tone: "emerald" },
  { v: "others", label: "Others", icon: Building2, tone: "slate" },
];

// 2026-10-22 (user request): kategori aset DINAMIS dari backend
// (/assets/categories). Konstanta di atas hanya fallback awal; ikon dipetakan
// per key, kategori custom memakai ikon default.
const CATEGORY_ICONS = { office: Package, vehicle: Car, machine: Cog, others: Building2 };
const resolveCats = (apiCats) => (Array.isArray(apiCats) && apiCats.length
  ? apiCats.map((c) => ({ v: c.key, label: c.label, icon: CATEGORY_ICONS[c.key] || Package, tone: c.tone || "slate", builtin: c.builtin, asset_count: c.asset_count }))
  : CATEGORIES);

const STATUSES = [
  { v: "active", label: "Aktif", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { v: "idle", label: "Tidak Digunakan", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { v: "loaned", label: "Dipinjamkan", cls: "bg-blue-100 text-blue-800 border-blue-300" },
  { v: "in_service", label: "Dalam Servis", cls: "bg-amber-100 text-amber-800 border-amber-300" },
  { v: "broken", label: "Rusak", cls: "bg-rose-100 text-rose-800 border-rose-300" },
  { v: "lost", label: "Hilang", cls: "bg-red-200 text-red-900 border-red-400" },
  { v: "archived", label: "Diarsipkan", cls: "bg-slate-200 text-slate-600 border-slate-400" },
];

const LOCATION_TYPES = [
  { v: "kantor", label: "Kantor" }, { v: "project", label: "Project" },
  { v: "gudang", label: "Gudang" }, { v: "vendor_servis", label: "Vendor/Servis" },
  { v: "lainnya", label: "Lainnya" },
];

// 149-ba: Movement lifecycle
const MOVEMENT_STATUSES = [
  { v: "planned", label: "Direncanakan", cls: "bg-slate-100 text-slate-700 border-slate-300" },
  { v: "in_transit", label: "Dalam Perjalanan", cls: "bg-sky-100 text-sky-800 border-sky-300" },
  { v: "received", label: "Diterima", cls: "bg-emerald-100 text-emerald-800 border-emerald-300" },
  { v: "returned", label: "Dikembalikan", cls: "bg-indigo-100 text-indigo-800 border-indigo-300" },
  { v: "cancelled", label: "Dibatalkan", cls: "bg-rose-100 text-rose-700 border-rose-300" },
];
const MOVEMENT_REASONS = [
  { v: "peminjaman", label: "Peminjaman" },
  { v: "rotasi", label: "Rotasi Divisi" },
  { v: "servis", label: "Kirim ke Servis" },
  { v: "kembali", label: "Kembali dari Servis" },
  { v: "pemindahan_gudang", label: "Pemindahan Gudang" },
  { v: "perbaikan", label: "Perbaikan" },
  { v: "penugasan", label: "Penugasan Project" },
  { v: "lain", label: "Lain-lain" },
];
// Timeline event kind → label + tone
const TIMELINE_KIND_META = {
  registration: { label: "Registrasi", tone: "bg-slate-100 text-slate-700 border-slate-300", icon: FileText },
  movement: { label: "Perpindahan", tone: "bg-orange-100 text-orange-800 border-orange-300", icon: ArrowRightLeft },
  movement_confirm: { label: "Konfirmasi Terima", tone: "bg-emerald-100 text-emerald-800 border-emerald-300", icon: Check },
  movement_return: { label: "Pengembalian", tone: "bg-indigo-100 text-indigo-800 border-indigo-300", icon: ArrowRightLeft },
  movement_cancel: { label: "Movement Batal", tone: "bg-rose-100 text-rose-700 border-rose-300", icon: X },
  movement_edit: { label: "Koreksi Movement", tone: "bg-amber-100 text-amber-800 border-amber-300", icon: Pencil },
  inspection: { label: "Pemeriksaan", tone: "bg-sky-100 text-sky-800 border-sky-300", icon: ClipboardCheck },
  service: { label: "Servis", tone: "bg-purple-100 text-purple-800 border-purple-300", icon: Wrench },
  status_change: { label: "Perubahan Status", tone: "bg-slate-100 text-slate-700 border-slate-300", icon: History },
  upload_photo: { label: "Upload Foto", tone: "bg-teal-100 text-teal-800 border-teal-300", icon: Camera },
  upload_document: { label: "Upload Dokumen", tone: "bg-teal-100 text-teal-800 border-teal-300", icon: Upload },
  delete_document: { label: "Hapus Dokumen", tone: "bg-rose-100 text-rose-700 border-rose-300", icon: Trash2 },
  update: { label: "Ubah Master", tone: "bg-slate-100 text-slate-700 border-slate-300", icon: Pencil },
  archive: { label: "Diarsipkan", tone: "bg-amber-100 text-amber-800 border-amber-300", icon: ArchiveIcon },
};

export default function AssetsPage() {
  const { user } = useAuth();
  const { canPerm } = useAccess();
  const [searchParams, setSearchParams] = useSearchParams();
  const [assets, setAssets] = useState([]);
  const [locations, setLocations] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [filterCat, setFilterCat] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterLoc, setFilterLoc] = useState("");
  const [filterPic, setFilterPic] = useState("");
  const [includeArchived, setIncludeArchived] = useState(false);
  // 149-cw-r85 (2026-08-27) — QoL toggle "PIC Saya": filter cepat ke aset yang
  // user sedang jadi PIC-nya. Persist ke localStorage supaya sticky per user.
  const [picSelf, setPicSelf] = useState(() => {
    try { return localStorage.getItem("assets_pic_self") === "1"; } catch { return false; }
  });
  useEffect(() => {
    try { localStorage.setItem("assets_pic_self", picSelf ? "1" : "0"); } catch { /* ignore */ }
  }, [picSelf]);
  const [formOpen, setFormOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [locOpen, setLocOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [bulkSelected, setBulkSelected] = useState(new Set());
  const [bulkQROpen, setBulkQROpen] = useState(false);
  // 2026-10-22: kategori dinamis + bulk move kategori.
  const [categories, setCategories] = useState(CATEGORIES);
  const [bulkCat, setBulkCat] = useState("");
  const [bulkCatBusy, setBulkCatBusy] = useState(false);
  // 2026-10-22 (user request): visibilitas kolom per user (tampil/hide) +
  // default global dari Pengaturan. Urutan sumber: override personal
  // (/user-prefs/table/assets) → default global (/settings/asset-columns)
  // → bawaan sistem (ASSET_DEFAULT_COLUMNS).
  const [visibleCols, setVisibleCols] = useState(ASSET_DEFAULT_COLUMNS);
  const [colDefault, setColDefault] = useState(ASSET_DEFAULT_COLUMNS);
  const [colPickerOpen, setColPickerOpen] = useState(false);
  const loadColPrefs = useCallback(async () => {
    let def = ASSET_DEFAULT_COLUMNS;
    try {
      const { data } = await api.get("/settings/asset-columns");
      if (Array.isArray(data?.columns) && data.columns.length) def = data.columns;
    } catch { /* fallback bawaan */ }
    setColDefault(def);
    try {
      const { data } = await api.get("/user-prefs/table/assets");
      if (Array.isArray(data?.columns) && data.columns.length) {
        setVisibleCols(data.columns.filter((k) => ASSET_COLUMN_DEFS.some((c) => c.key === k)));
        return;
      }
    } catch { /* belum ada override personal */ }
    setVisibleCols(def);
  }, []);
  const toggleCol = (key) => {
    setVisibleCols((cur) => {
      // pertahankan urutan kanonik ASSET_COLUMN_DEFS agar kolom konsisten
      const next = ASSET_COLUMN_DEFS.map((d) => d.key)
        .filter((k) => (k === key ? !cur.includes(k) : cur.includes(k)));
      api.put("/user-prefs/table/assets", { columns: next }).catch(() => {});
      return next;
    });
  };
  const resetCols = () => {
    setVisibleCols(colDefault);
    api.delete("/user-prefs/table/assets").catch(() => {});
  };

  const canView = canPerm(user, "assets", "view");
  const canCreate = canPerm(user, "assets", "create");
  const canManageLoc = canPerm(user, "asset_locations", "update");
  const canArchive = canPerm(user, "assets", "update") || canPerm(user, "assets", "delete");
  const canExport = canPerm(user, "assets", "view");
  // 149-cw-r84 — PIC-only access: user tanpa matrix view TAPI jadi PIC di
  // ≥1 aset masih boleh masuk (backend akan filter otomatis). Ubah gating
  // supaya halaman tetap render → biarkan fetch /assets menentukan.
  const [picOnlyAccess, setPicOnlyAccess] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { include_archived: includeArchived };
      if (filterCat) params.category = filterCat;
      if (filterStatus) params.status = filterStatus;
      if (filterLoc) params.location_id = filterLoc;
      // 149-cw-r85: picSelf toggle override filterPic bila aktif (klausa OR).
      if (picSelf && user?.id) params.pic_id = user.id;
      else if (filterPic) params.pic_id = filterPic;
      if (q) params.q = q;
      const { data } = await api.get("/assets", { params });
      setAssets(data);
      // 149-cw-r84: kalau backend berhasil respon walau frontend perm=false,
      // berarti user PIC-only. Aktifkan render halaman.
      if (!canView && Array.isArray(data)) setPicOnlyAccess(true);
    } catch (e) {
      toast.error(formatDetail(e.response?.data?.detail));
    } finally { setLoading(false); }
  }, [filterCat, filterStatus, filterLoc, filterPic, q, includeArchived, canView, picSelf, user?.id]);

  const loadLocations = useCallback(async () => {
    try { const { data } = await api.get("/assets/locations"); setLocations(data); } catch {}
  }, []);
  const loadCategories = useCallback(async () => {
    try { const { data } = await api.get("/assets/categories"); setCategories(resolveCats(data)); } catch {}
  }, []);

  // 2026-10-22: bulk move — pindahkan beberapa aset sekaligus ke kategori lain.
  const applyBulkCategory = async () => {
    if (!bulkCat) { toast.error("Pilih kategori tujuan dulu"); return; }
    if (!bulkSelected.size) { toast.error("Pilih minimal 1 aset"); return; }
    setBulkCatBusy(true);
    try {
      const { data } = await api.post("/assets/bulk-category", { asset_ids: Array.from(bulkSelected), category: bulkCat });
      toast.success(`${data.updated} aset dipindahkan ke kategori baru`);
      setBulkSelected(new Set()); setBulkCat("");
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBulkCatBusy(false); }
  };
  const loadUsers = useCallback(async () => {
    // 16-ASSET-PIC-LOCATION-R1: pakai endpoint publik `/assets/pic-directory`
    // yg hanya butuh `assets:view` (bukan admin/personnel gate) → dropdown PIC
    // tidak kosong untuk user non-admin.
    try {
      const { data } = await api.get("/assets/pic-directory");
      setUsers((data?.users || []).map((u) => ({ id: u.id, name: u.name, division: u.division, level: u.level })));
    } catch {
      // Fallback ke endpoint lama bila endpoint baru belum di-deploy
      try {
        const { data } = await api.get("/admin/users");
        setUsers((data || []).map((u) => ({ id: u.id, name: u.name, division: u.division })));
      } catch {
        try {
          const { data } = await api.get("/personnel");
          setUsers((data || []).map((u) => ({ id: u.id, name: u.name, division: u.division })));
        } catch {}
      }
    }
  }, []);

  useEffect(() => { loadLocations(); loadUsers(); loadCategories(); loadColPrefs(); }, [loadLocations, loadUsers, loadCategories, loadColPrefs]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && assets.length) {
      const a = assets.find((x) => x.id === openId);
      if (a) { setSelectedAsset(a); setDetailOpen(true); }
      else {
        // Deep-link menunjuk asset yg sudah terhapus / di luar scope.
        toast.info("Item tidak ditemukan — mungkin sudah dihapus atau di luar akses Anda.", { id: "deeplink-not-found" });
      }
      // Clear param supaya refresh tidak trigger ulang.
      const next = new URLSearchParams(searchParams);
      next.delete("open");
      setSearchParams(next, { replace: true });
    }
  }, [searchParams, assets]);

  const locMap = useMemo(() => Object.fromEntries(locations.map((l) => [l.id, l])), [locations]);
  const userMap = useMemo(() => Object.fromEntries(users.map((u) => [u.id, u])), [users]);

  const exportCsv = async () => {
    try {
      const { data } = await api.get("/assets/export/csv");
      const rows = data.rows || [];
      if (!rows.length) { toast.error("Tidak ada data"); return; }
      const headers = Object.keys(rows[0]);
      const csv = [headers.join(","), ...rows.map((r) => headers.map((h) => JSON.stringify(r[h] ?? "")).join(","))].join("\n");
      const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url; a.download = `assets_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click(); URL.revokeObjectURL(url);
      toast.success("Export berhasil");
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const resetFilters = () => { setQ(""); setFilterCat(""); setFilterStatus(""); setFilterLoc(""); setFilterPic(""); setIncludeArchived(false); setPicSelf(false); };

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="assets-page">
      {!canView && !picOnlyAccess ? (
        <div className="p-8 text-center text-slate-500 border border-slate-200 bg-slate-50" data-testid="assets-no-access">
          <div className="text-lg font-semibold text-[#0B1D3A] mb-1">Akses Ditolak</div>
          <div className="text-sm">Anda tidak memiliki akses Asset Management. Modul ini dinonaktifkan oleh Access Control.</div>
        </div>
      ) : (<>
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <p className="overline text-slate-500">Asset Management</p>
          <h1 className="font-display text-3xl text-[#0B1D3A]">Inventaris &amp; Aset</h1>
          <p className="text-sm text-slate-500 mt-1">Office · Kendaraan · Mesin · Others · Semua histori terekam otomatis.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {bulkSelected.size > 0 && (
            <>
              {/* 2026-10-22 (user request): pindahkan beberapa aset sekaligus
                  ke kategori lain. */}
              <span className="inline-flex items-center gap-1.5" data-testid="assets-bulk-category-bar">
                <select value={bulkCat} onChange={(e) => setBulkCat(e.target.value)} data-testid="assets-bulk-category-select"
                  className="h-9 border border-slate-300 text-xs px-2 bg-white">
                  <option value="">Pindah ke Kategori…</option>
                  {categories.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
                </select>
                <button type="button" data-testid="btn-bulk-category-apply" onClick={applyBulkCategory} disabled={bulkCatBusy}
                  className="inline-flex items-center gap-1.5 h-9 px-3 bg-emerald-700 text-white hover:bg-emerald-800 uppercase text-xs tracking-wider font-semibold disabled:opacity-50">
                  <ArrowRightLeft size={13}/> Terapkan ({bulkSelected.size})
                </button>
              </span>
              <button type="button" data-testid="btn-bulk-qr-print" onClick={() => setBulkQROpen(true)}
                className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#0B1D3A] text-white hover:bg-slate-800 uppercase text-xs tracking-wider font-semibold">
                <QrCode size={13}/> Cetak QR ({bulkSelected.size})
              </button>
              <button
                type="button"
                data-testid="btn-bulk-card-print"
                onClick={() => {
                  const chosen = assets.filter((a) => bulkSelected.has(a.id));
                  if (!chosen.length) { toast.error("Pilih minimal 1 aset"); return; }
                  printAssetCardsBulk(chosen, locMap, userMap, { title: "Kartu Aset — ENS" });
                }}
                className="inline-flex items-center gap-1.5 h-9 px-3 bg-indigo-700 text-white hover:bg-indigo-800 uppercase text-xs tracking-wider font-semibold"
              >
                <Layers size={13}/> Cetak Kartu ({bulkSelected.size})
              </button>
            </>
          )}
          {canExport && (
            <>
              <button
                type="button"
                data-testid="btn-export-xlsx"
                onClick={() => {
                  if (!assets.length) { toast.error("Tidak ada data untuk di-export"); return; }
                  exportAssetsToExcel(assets, locMap, userMap, { fileName: "inventaris-aset", title: "Inventaris & Aset — ENS" });
                }}
                className="inline-flex items-center gap-1.5 h-9 px-3 border border-emerald-600 text-emerald-700 bg-white hover:bg-emerald-50 uppercase text-xs tracking-wider font-semibold"
              >
                <FileSpreadsheet size={13}/> Export Excel
              </button>
              <button
                type="button"
                data-testid="btn-print-pdf"
                onClick={() => {
                  if (!assets.length) { toast.error("Tidak ada data untuk dicetak"); return; }
                  printAssetsToPDF(assets, locMap, userMap, { title: "Inventaris & Aset — ENS", subtitle: `${assets.length} aset · filter aktif · Dicetak ${new Date().toLocaleString("id-ID")}` });
                }}
                className="inline-flex items-center gap-1.5 h-9 px-3 border border-rose-600 text-rose-700 bg-white hover:bg-rose-50 uppercase text-xs tracking-wider font-semibold"
              >
                <Printer size={13}/> Print PDF
              </button>
              <button type="button" data-testid="btn-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 uppercase text-xs tracking-wider font-semibold" title="Export mentah CSV (untuk import ulang)"><Download size={13}/> CSV</button>
            </>
          )}
          {canManageLoc && <button type="button" data-testid="btn-manage-locations" onClick={() => setLocOpen(true)} className="inline-flex items-center gap-1.5 h-9 px-3 border border-slate-300 bg-white text-slate-700 hover:bg-slate-50 uppercase text-xs tracking-wider font-semibold"><MapPin size={13}/> Kelola Lokasi</button>}
          {canCreate && <button type="button" data-testid="btn-new-asset" onClick={() => { setSelectedAsset(null); setFormOpen(true); }} className="inline-flex items-center gap-1.5 h-9 px-3 bg-[#FF5A1F] text-white hover:bg-[#e04a13] uppercase text-xs tracking-wider font-semibold"><Plus size={13}/> Asset Baru</button>}
        </div>
      </div>

      <div className="bg-white border border-slate-200 p-3 mb-3 flex items-center gap-2 flex-wrap">
        <div className="relative">
          <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari nama/nomor/serial/brand…" data-testid="assets-search"
            className="pl-8 pr-2 py-2 border border-slate-300 text-xs w-72 focus:outline-none focus:border-[#FF5A1F]" />
        </div>
        <select value={filterCat} onChange={(e) => setFilterCat(e.target.value)} data-testid="assets-filter-category" className="border border-slate-300 text-xs px-2 py-2 bg-white">
          <option value="">Semua Kategori</option>
          {categories.map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
        </select>
        {/* 2026-10-22 (user request): show/hide kolom per user. */}
        <div className="relative">
          <button type="button" data-testid="assets-col-picker-btn" onClick={() => setColPickerOpen((v) => !v)}
            className="border border-slate-300 text-xs px-2 py-2 bg-white flex items-center gap-1 hover:bg-slate-50">
            <Columns3 size={12}/> Kolom
          </button>
          {colPickerOpen && (
            <div className="absolute z-30 mt-1 right-0 bg-white border border-slate-300 shadow-lg w-[250px] p-2 space-y-0.5" data-testid="assets-col-picker">
              <div className="text-[9px] uppercase tracking-wider text-slate-500 font-semibold pb-1 border-b border-slate-100">Tampilkan Kolom (tersimpan per akun)</div>
              {ASSET_COLUMN_DEFS.map((d) => (
                <label key={d.key} data-testid={`assets-col-toggle-${d.key}`} className="flex items-center gap-2 px-1 py-1 text-[11px] cursor-pointer hover:bg-slate-50">
                  <input type="checkbox" checked={visibleCols.includes(d.key)} onChange={() => toggleCol(d.key)} />
                  {d.label}
                </label>
              ))}
              <button type="button" data-testid="assets-col-reset" onClick={resetCols}
                className="mt-1 w-full text-[10px] uppercase tracking-wider font-semibold border border-slate-300 py-1 hover:bg-slate-50">
                Reset ke Default
              </button>
            </div>
          )}
        </div>
        <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} data-testid="assets-filter-status" className="border border-slate-300 text-xs px-2 py-2 bg-white">
          <option value="">Semua Status</option>
          {STATUSES.filter((s) => s.v !== "archived").map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
        </select>
        <select value={filterLoc} onChange={(e) => setFilterLoc(e.target.value)} data-testid="assets-filter-location" className="border border-slate-300 text-xs px-2 py-2 bg-white max-w-[200px]">
          <option value="">Semua Lokasi</option>
          {/* Filter dropdown tetap flat (bukan grouped) — hanya manual+aktif untuk filter cepat. */}
          {locations.filter((l) => l.is_active).map((l) => <option key={l.id} value={l.id}>{l.name}</option>)}
        </select>
        <select value={filterPic} onChange={(e) => setFilterPic(e.target.value)} data-testid="assets-filter-pic" className="border border-slate-300 text-xs px-2 py-2 bg-white max-w-[180px]">
          <option value="">Semua PIC</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
        </select>
        <label className="inline-flex items-center gap-1.5 text-xs text-slate-600 ml-2">
          <input type="checkbox" data-testid="assets-include-archived" checked={includeArchived} onChange={(e) => setIncludeArchived(e.target.checked)} />
          Sertakan arsip
        </label>
        {/* 149-cw-r85 (2026-08-27) — Toggle QoL "PIC Saya": filter aset yang
            user sedang menjadi PIC-nya. Sticky per user via localStorage. */}
        {user?.id && (
          <button
            type="button"
            data-testid="assets-toggle-pic-self"
            onClick={() => setPicSelf((v) => !v)}
            className={`inline-flex items-center gap-1 h-8 px-2.5 text-[10px] uppercase tracking-wider font-semibold border transition-colors ${picSelf ? "bg-indigo-600 border-indigo-600 text-white" : "bg-white border-slate-300 text-slate-700 hover:bg-slate-50"}`}
            title="Toggle: hanya tampilkan aset yang saya jadi PIC-nya"
          >
            <UserIcon size={11}/> PIC Saya{picSelf ? " ✓" : ""}
          </button>
        )}
        <button type="button" onClick={resetFilters} data-testid="assets-reset-filter" className="ml-auto text-[10px] uppercase tracking-wider font-semibold text-slate-600 hover:text-[#FF5A1F]">Reset Filter</button>
      </div>

      {/* 149-bx: Assets tidak punya StickyContextBar → pakai `-nav` (offset =
          global-topbar-height saja). Sebelumnya salah pakai `ens-sticky-thead`
          yg offset-nya nav+ctx sehingga saat scroll header terdorong turun 2rem. */}
      {/* 2026-10-22 (user request: judul header tumpang tindih & tidak stick):
          page-level sticky th GAGAL karena wrapper `overflow-x:auto` menjadikan
          wrapper scroll container — sticky th tidak bisa escape ke viewport dan
          tidak punya ruang gerak di dalamnya → header ikut terscroll + offset
          salah tumpang tindih dgn baris. Fix: wrapper sendiri yg sticky di bawah
          topbar + scroll vertikal internal; header sticky top:0 relatif wrapper
          (lihat .assets-table-sticky-wrap di index.css). */}
      <div className="bg-white border border-slate-200 ens-table-scroll assets-table-sticky-wrap">
        <table className="w-full text-xs">
          <thead className="bg-slate-50 border-b-2 border-slate-300 ens-sticky-thead-nav">
            <tr className="text-left">
              <th className="px-3 py-2.5 w-[36px]">
                <input type="checkbox" data-testid="assets-select-all"
                  checked={assets.length > 0 && bulkSelected.size === assets.length}
                  onChange={(e) => setBulkSelected(e.target.checked ? new Set(assets.map((a) => a.id)) : new Set())}
                  onClick={(e) => e.stopPropagation()}/>
              </th>
              <th className="px-3 py-2.5 overline text-slate-700 w-[120px] resize-x overflow-hidden">No. Asset</th>
              <th className="px-3 py-2.5 overline text-slate-700 min-w-[240px] resize-x overflow-hidden">Nama</th>
              {/* 2026-10-22 (user request): kolom tengah dinamis sesuai
                  visibilitas pilihan user (visibleCols). */}
              {visibleCols.map((ck) => (
                <th key={ck} className="px-3 py-2.5 overline text-slate-700 whitespace-nowrap">{ASSET_COL_LABEL[ck]}</th>
              ))}
              <th className="px-3 py-2.5 overline text-slate-700 w-[100px]">Aksi</th>
            </tr>
          </thead>
          <tbody data-testid="assets-table">
            {loading && <tr><td colSpan={4 + visibleCols.length} className="text-center py-6 text-slate-400">Memuat…</td></tr>}
            {/* 149-bx: Empty state — kompak, tidak absolute/translate/negative-margin.
                Tetap 1 <tr> dgn colSpan penuh supaya berada tepat di bawah header
                kolom. Padding vertikal ringan (py-8) supaya tidak ada ruang kosong
                besar seperti sebelumnya (py-12 → dikurangi). */}
            {!loading && assets.length === 0 && (
              <tr><td colSpan={4 + visibleCols.length} className="text-center py-8" data-testid="assets-empty">
                <div className="flex flex-col items-center gap-2 text-slate-400">
                  <Package size={28} />
                  <div className="text-sm font-semibold text-slate-700">Belum ada asset</div>
                  <div className="text-xs">Klik "Asset Baru" untuk menambah inventaris pertama</div>
                </div>
              </td></tr>
            )}
            {!loading && assets.map((a) => {
              const cat = categories.find((c) => c.v === a.category);
              const st = STATUSES.find((s) => s.v === a.status);
              const CatIcon = cat?.icon || Package;
              const checked = bulkSelected.has(a.id);
              // 2026-10-22: render sel kolom dinamis sesuai visibleCols.
              const renderCell = (ck) => {
                if (ck === "category") return (
                  <td key={ck} className="px-3 py-2.5">{cat
                    ? <span className="inline-flex items-center gap-1"><CatIcon size={11} className="text-slate-500"/>{cat.label}</span>
                    : <span className="text-slate-300" title="Kategori sudah dihapus">—</span>}</td>
                );
                if (ck === "status") return (
                  <td key={ck} className="px-3 py-2.5">
                    {st && <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${st.cls}`}>{st.label}</span>}
                  </td>
                );
                const v = assetCellValue(a, ck);
                return <td key={ck} className="px-3 py-2.5 text-slate-700 whitespace-nowrap">{v ?? <span className="text-slate-300">—</span>}</td>;
              };
              return (
                <tr key={a.id} data-testid={`assets-row-${a.id}`}
                    className={`border-b border-slate-100 hover:bg-slate-50 ${checked ? "bg-orange-50/40" : ""}`}>
                  <td className="px-3 py-2.5" onClick={(e) => e.stopPropagation()}>
                    <input type="checkbox" data-testid={`assets-check-${a.id}`} checked={checked}
                      onChange={() => setBulkSelected((s) => { const n = new Set(s); n.has(a.id) ? n.delete(a.id) : n.add(a.id); return n; })}/>
                  </td>
                  <td className="px-3 py-2.5 tabular-nums font-medium text-[#0B1D3A] cursor-pointer" onClick={() => { setSelectedAsset(a); setDetailOpen(true); }}>{a.asset_number}</td>
                  <td className="px-3 py-2.5 font-medium text-[#0B1D3A] cursor-pointer" onClick={() => { setSelectedAsset(a); setDetailOpen(true); }}>
                    <div className="truncate max-w-[380px]" title={a.name}>{a.name}</div>
                    {a.serial_number && <div className="text-[10px] text-slate-500">SN: {a.serial_number}</div>}
                  </td>
                  {/* 2026-10-22: kolom dinamis — kategori yg dihapus admin tetap
                      tampil kosong ("—"), data aset utuh. */}
                  {visibleCols.map((ck) => renderCell(ck))}
                  <td className="px-3 py-2.5">
                    <button type="button" data-testid={`assets-view-${a.id}`} onClick={(e) => { e.stopPropagation(); setSelectedAsset(a); setDetailOpen(true); }} className="p-1 border border-slate-300 text-slate-700 hover:bg-slate-50" title="Lihat detail"><FileText size={11}/></button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <AssetFormDialog open={formOpen} onOpenChange={setFormOpen} initial={selectedAsset} locations={locations} users={users} categories={categories}
        onSaved={() => { setFormOpen(false); setSelectedAsset(null); load(); }} />
      <AssetDetailDialog open={detailOpen} onOpenChange={setDetailOpen} asset={selectedAsset} users={users} locations={locations} categories={categories}
        canUpdate={canPerm(user, "assets", "update")} canArchive={canArchive}
        onChanged={() => { load(); if (selectedAsset) api.get(`/assets/${selectedAsset.id}`).then(({data}) => setSelectedAsset(data)).catch(()=>{}); }}
        onEdit={() => { setDetailOpen(false); setFormOpen(true); }} />
      <LocationManagerDialog open={locOpen} onOpenChange={setLocOpen} onReload={loadLocations} locations={locations} canManage={canManageLoc} />
      <BulkQRDialog open={bulkQROpen} onOpenChange={setBulkQROpen}
        assets={assets.filter((a) => bulkSelected.has(a.id))}
        onClear={() => setBulkSelected(new Set())} />
      </>)}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Asset Form Dialog — with dynamic fields per category
// ---------------------------------------------------------------------------
const emptyAsset = {
  asset_number: "", name: "", category: "office", brand: "", type_model: "", serial_number: "",
  purchase_date: "", purchase_value: null, description: "", status: "active",
  pic_id: "", location_id: "", location_name: "", photo_url: "", documents: [], custom_notes: "",
  // 149-cw-r84: interval pemeriksaan (hari). Kosong = tanpa jadwal.
  inspection_interval_days: null, last_inspection_date: "",
  vehicle: { plate_number: "", vin: "", engine_number: "", tax_due_date: "", stnk_expiry_date: "", insurance_policy_no: "", insurance_expiry_date: "" },
  machine: { model_serial: "", capacity_spec: "", service_schedule: "", calibration_schedule: "" },
};

function AssetFormDialog({ open, onOpenChange, initial, locations, users, categories = CATEGORIES, onSaved }) {
  const [f, setF] = useState(emptyAsset);
  const [busy, setBusy] = useState(false);
  // 16-ASSET-PIC-LOCATION-R1: hybrid location groups for combobox datalist
  const { groups: locGroups, loading: locLoading } = useLocationOptions();
  useEffect(() => {
    if (!open) return;
    if (initial) {
      // Derive readable location_name from location_id (via passed `locations` list)
      // or fallback to `initial.location_name` set by list endpoint.
      const locObj = locations.find((l) => l.id === initial.location_id);
      const locName = locObj?.name || initial.location_name || "";
      setF({
        ...emptyAsset, ...initial, location_name: locName,
        vehicle: { ...emptyAsset.vehicle, ...(initial.vehicle || {}) },
        machine: { ...emptyAsset.machine, ...(initial.machine || {}) },
      });
    } else {
      // 2026-10-22: default kategori = kategori aktif pertama (bila "office"
      // dihapus admin, jangan default ke kategori yang sudah tidak ada).
      setF({ ...emptyAsset, category: categories?.[0]?.v || emptyAsset.category });
    }
  }, [open, initial, locations, categories]);
  const set = (k, v) => setF((cur) => ({ ...cur, [k]: v }));
  const setNested = (parent, k, v) => setF((cur) => ({ ...cur, [parent]: { ...cur[parent], [k]: v } }));

  // Group active (non-disabled) users by division for PIC dropdown.
  const picGroups = useMemo(() => {
    const map = {};
    users.forEach((u) => {
      const div = u.division || "Lain-lain";
      (map[div] = map[div] || []).push(u);
    });
    return Object.keys(map).sort().map((d) => ({ division: d, items: map[d] }));
  }, [users]);

  const save = async () => {
    if (!f.asset_number.trim() || !f.name.trim()) { toast.error("Nomor & nama wajib diisi"); return; }
    if (f.category === "vehicle" && !f.vehicle?.plate_number?.trim()) { toast.error("Nomor polisi wajib untuk Kendaraan"); return; }
    setBusy(true);
    try {
      // 16-ASSET-PIC-LOCATION-R1: resolve location_name → id (create if new).
      // If user cleared name → send empty id (unset). Else backend idempotent.
      const nameClean = (f.location_name || "").trim();
      const resolvedLocationId = nameClean ? await resolveOrCreateLocationByName(nameClean) : "";
      const payload = {
        ...f, location_id: resolvedLocationId,
        purchase_value: f.purchase_value ? Number(f.purchase_value) : null,
        inspection_interval_days: f.inspection_interval_days ? Number(f.inspection_interval_days) : null,
        last_inspection_date: f.last_inspection_date || null,
      };
      delete payload.location_name; // transient UI-only field
      if (initial?.id) { await api.patch(`/assets/${initial.id}`, payload); toast.success("Asset diperbarui"); }
      else { await api.post("/assets", payload); toast.success("Asset dibuat"); }
      onSaved();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="asset-form-dialog">
        <DialogHeader><DialogTitle>{initial ? "Ubah Asset" : "Asset Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline text-[10px] text-slate-500">No. Asset *</label>
              <input data-testid="asset-number" value={f.asset_number} onChange={(e) => set("asset_number", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
            <div><label className="overline text-[10px] text-slate-500">Kategori *</label>
              <select data-testid="asset-category" value={f.category} onChange={(e) => set("category", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white">
                {(categories?.length ? categories : CATEGORIES).map((c) => <option key={c.v} value={c.v}>{c.label}</option>)}
              </select></div>
          </div>
          <div><label className="overline text-[10px] text-slate-500">Nama Asset *</label>
            <input data-testid="asset-name" value={f.name} onChange={(e) => set("name", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="overline text-[10px] text-slate-500">Brand</label>
              <input value={f.brand} onChange={(e) => set("brand", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
            <div><label className="overline text-[10px] text-slate-500">Tipe/Model</label>
              <input value={f.type_model} onChange={(e) => set("type_model", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
            <div><label className="overline text-[10px] text-slate-500">Serial Number</label>
              <input value={f.serial_number} onChange={(e) => set("serial_number", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div><label className="overline text-[10px] text-slate-500">Tanggal Pembelian</label>
              <DateField value={f.purchase_date || ""} onChange={(v) => set("purchase_date", v)} testid="asset-purchase-date"/></div>
            <div><label className="overline text-[10px] text-slate-500">Nilai Pembelian (Rp)</label>
              <input type="number" value={f.purchase_value || ""} onChange={(e) => set("purchase_value", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
            <div><label className="overline text-[10px] text-slate-500">Status</label>
              <select data-testid="asset-status" value={f.status} onChange={(e) => set("status", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white">
                {STATUSES.filter((s) => s.v !== "archived").map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="overline text-[10px] text-slate-500">Lokasi Saat Ini</label>
              <LocationCombo testid="asset-location" value={f.location_name || ""} onChange={(v) => set("location_name", v)}
                className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white"
                groups={locGroups} loading={locLoading} />
              <div className="text-[10px] text-slate-400 italic mt-0.5">Ketik nama lokasi baru untuk membuat otomatis, atau pilih dari daftar.</div></div>
            <div><label className="overline text-[10px] text-slate-500">PIC Saat Ini</label>
              <select data-testid="asset-pic" value={f.pic_id || ""} onChange={(e) => set("pic_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white">
                <option value="">— pilih PIC —</option>
                {picGroups.map((g) => (
                  <optgroup key={g.division} label={g.division}>
                    {g.items.map((u) => <option key={u.id} value={u.id}>{u.name}{u.level ? ` · Lv${u.level}` : ""}</option>)}
                  </optgroup>
                ))}
              </select></div>
          </div>
          <div><label className="overline text-[10px] text-slate-500">Foto Utama</label>
            <PhotoUploader assetId={initial?.id} value={f.photo_url} onChange={(url) => set("photo_url", url)} disabled={!initial} />
            {!initial && <div className="text-[10px] text-slate-400 italic mt-1">Simpan dulu asset baru, lalu upload foto dari Detail.</div>}
          </div>
          <div><label className="overline text-[10px] text-slate-500">Deskripsi</label>
            <textarea value={f.description || ""} onChange={(e) => set("description", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>

          {/* 149-cw-r84 (2026-08-27) — Interval Pemeriksaan Berkala.
              Preset 30/90/180/365 hari. Reminder muncul di Pusat Pengingat ke
              PIC saat (last_inspection_date + interval) ≤ hari ini. */}
          <div className="border border-indigo-300 bg-indigo-50/40 p-3 space-y-2" data-testid="inspection-fields">
            <div className="overline text-[10px] text-indigo-800 flex items-center gap-1"><ClipboardCheck size={11}/> Jadwal Pemeriksaan Berkala</div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="overline text-[10px] text-slate-500">Interval Pemeriksaan</label>
                <select
                  data-testid="asset-inspection-interval"
                  value={f.inspection_interval_days ?? ""}
                  onChange={(e) => set("inspection_interval_days", e.target.value === "" ? null : Number(e.target.value))}
                  className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm bg-white"
                >
                  <option value="">— Tanpa jadwal —</option>
                  <option value="30">Setiap 30 hari (bulanan)</option>
                  <option value="90">Setiap 90 hari (per kuartal)</option>
                  <option value="180">Setiap 180 hari (per semester)</option>
                  <option value="365">Setiap 365 hari (per tahun)</option>
                </select>
                <div className="text-[10px] text-slate-400 italic mt-0.5">Reminder otomatis ke PIC saat lewat interval.</div>
              </div>
              <div>
                <label className="overline text-[10px] text-slate-500">Pemeriksaan Terakhir</label>
                <DateField value={f.last_inspection_date || ""} onChange={(v) => set("last_inspection_date", v)} testid="asset-last-inspection"/>
                <div className="text-[10px] text-slate-400 italic mt-0.5">Baseline hitung mundur reminder. Auto-update saat submit pemeriksaan.</div>
              </div>
            </div>
          </div>

          {f.category === "vehicle" && (
            <div className="border border-orange-300 bg-orange-50/40 p-3 space-y-2" data-testid="vehicle-fields">
              <div className="overline text-[10px] text-orange-800 flex items-center gap-1"><Car size={11}/> Field Kendaraan Bermotor</div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="overline text-[10px] text-slate-500">No. Polisi *</label>
                  <input data-testid="vehicle-plate" value={f.vehicle.plate_number} onChange={(e) => setNested("vehicle", "plate_number", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
                <div><label className="overline text-[10px] text-slate-500">VIN/No. Rangka</label>
                  <input value={f.vehicle.vin} onChange={(e) => setNested("vehicle", "vin", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
                <div><label className="overline text-[10px] text-slate-500">No. Mesin</label>
                  <input value={f.vehicle.engine_number} onChange={(e) => setNested("vehicle", "engine_number", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div><label className="overline text-[10px] text-slate-500">Jatuh Tempo Pajak</label>
                  <DateField value={f.vehicle.tax_due_date || ""} onChange={(v) => setNested("vehicle", "tax_due_date", v)} testid="vehicle-tax"/></div>
                <div><label className="overline text-[10px] text-slate-500">STNK Berlaku Sampai</label>
                  <DateField value={f.vehicle.stnk_expiry_date || ""} onChange={(v) => setNested("vehicle", "stnk_expiry_date", v)}/></div>
                <div><label className="overline text-[10px] text-slate-500">Asuransi (opsional)</label>
                  <input value={f.vehicle.insurance_policy_no} onChange={(e) => setNested("vehicle", "insurance_policy_no", e.target.value)} placeholder="No. polis" className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
              </div>
            </div>
          )}

          {f.category === "machine" && (
            <div className="border border-emerald-300 bg-emerald-50/40 p-3 space-y-2" data-testid="machine-fields">
              <div className="overline text-[10px] text-emerald-800 flex items-center gap-1"><Cog size={11}/> Field Mesin</div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-[10px] text-slate-500">Model/Serial</label>
                  <input value={f.machine.model_serial} onChange={(e) => setNested("machine", "model_serial", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
                <div><label className="overline text-[10px] text-slate-500">Kapasitas/Spesifikasi</label>
                  <input value={f.machine.capacity_spec} onChange={(e) => setNested("machine", "capacity_spec", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div><label className="overline text-[10px] text-slate-500">Jadwal Servis (opsional)</label>
                  <input value={f.machine.service_schedule} onChange={(e) => setNested("machine", "service_schedule", e.target.value)} placeholder="mis. Setiap 3 bulan" className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
                <div><label className="overline text-[10px] text-slate-500">Jadwal Kalibrasi (opsional)</label>
                  <input value={f.machine.calibration_schedule} onChange={(e) => setNested("machine", "calibration_schedule", e.target.value)} placeholder="mis. Tahunan" className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
              </div>
            </div>
          )}

          {/* 2026-10-22: catatan custom untuk SEMUA kategori selain
              kendaraan/mesin — termasuk kategori custom dari Pengaturan. */}
          {(f.category !== "vehicle" && f.category !== "machine") && (
            <div><label className="overline text-[10px] text-slate-500">Catatan Custom</label>
              <textarea data-testid="asset-custom-notes" value={f.custom_notes || ""} onChange={(e) => set("custom_notes", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-sm"/></div>
          )}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => onOpenChange(false)} className="px-3 py-1.5 border border-slate-300 text-xs uppercase tracking-wider text-slate-700 hover:bg-slate-50">Batal</button>
          <button type="button" data-testid="asset-save" disabled={busy} onClick={save} className="px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#e04a13]">{busy ? "Menyimpan…" : "Simpan"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Asset Detail Dialog with tabs
// ---------------------------------------------------------------------------
const DETAIL_TABS = [
  { id: "overview", label: "Overview", icon: FileText },
  { id: "photos", label: "Foto", icon: Camera },
  { id: "qrcode", label: "QR Code", icon: QrCode },
  { id: "timeline", label: "Riwayat/Timeline", icon: History },
  { id: "movements", label: "Perpindahan", icon: ArrowRightLeft },
  { id: "inspections", label: "Pemeriksaan", icon: ClipboardCheck },
  { id: "services", label: "Servis & Perbaikan", icon: Wrench },
  { id: "documents", label: "Dokumen", icon: FileText },
  { id: "audit", label: "Audit Log", icon: ShieldAlert },
];

function AssetDetailDialog({ open, onOpenChange, asset, users, locations, categories = CATEGORIES, canUpdate, canArchive, onChanged, onEdit }) {
  const [tab, setTab] = useState("overview");
  const [audit, setAudit] = useState([]);
  const [confirmArchive, setConfirmArchive] = useState(false);

  useEffect(() => {
    if (!open || !asset) return;
    setTab("overview");
    api.get(`/assets/${asset.id}/audit`).then(({data}) => setAudit(data)).catch(() => {});
  }, [open, asset]);

  if (!asset) return null;

  const cat = CATEGORIES.find((c) => c.v === asset.category);
  const st = STATUSES.find((s) => s.v === asset.status);

  const doArchive = async () => {
    try { await api.post(`/assets/${asset.id}/archive`); toast.success("Asset diarsipkan"); setConfirmArchive(false); onOpenChange(false); onChanged(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="asset-detail-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            {asset.name}
            {st && <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${st.cls}`}>{st.label}</span>}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">{asset.asset_number} · {cat?.label}</DialogDescription>
        </DialogHeader>
        <div className="flex items-center gap-1 border-b border-slate-200 -mx-6 px-6 overflow-x-auto">
          {DETAIL_TABS.map((t) => {
            const active = t.id === tab;
            return (
              <button key={t.id} type="button" data-testid={`detail-tab-${t.id}`} onClick={() => setTab(t.id)}
                className={`px-3 py-2 text-[10px] uppercase tracking-wider font-semibold border-b-2 inline-flex items-center gap-1 whitespace-nowrap ${active ? "border-[#FF5A1F] text-[#FF5A1F]" : "border-transparent text-slate-500 hover:text-[#0B1D3A]"}`}>
                <t.icon size={11}/> {t.label}
              </button>
            );
          })}
        </div>
        <div className="pt-3 min-h-[300px]">
          {tab === "overview" && (
            <div className="space-y-2 text-sm" data-testid="tab-overview">
              <Row label="No. Asset" v={asset.asset_number}/>
              <Row label="Kategori" v={cat?.label}/>
              <Row label="Brand" v={asset.brand}/>
              <Row label="Tipe/Model" v={asset.type_model}/>
              <Row label="Serial" v={asset.serial_number}/>
              <Row label="PIC" v={asset.pic_name}/>
              <Row label="Lokasi" v={asset.location_name}/>
              <Row label="Tgl Pembelian" v={asset.purchase_date && fmtDate(asset.purchase_date)}/>
              <Row label="Nilai Pembelian" v={asset.purchase_value ? `Rp ${Number(asset.purchase_value).toLocaleString("id-ID")}` : ""}/>
              {asset.category === "vehicle" && asset.vehicle && (
                <>
                  <div className="mt-2 pt-2 border-t border-slate-100 overline text-[10px] text-slate-500">Kendaraan</div>
                  <Row label="No. Polisi" v={asset.vehicle.plate_number}/>
                  <Row label="VIN" v={asset.vehicle.vin}/>
                  <Row label="No. Mesin" v={asset.vehicle.engine_number}/>
                  <Row label="Tempo Pajak" v={asset.vehicle.tax_due_date && fmtDate(asset.vehicle.tax_due_date)}/>
                  <Row label="STNK Berlaku" v={asset.vehicle.stnk_expiry_date && fmtDate(asset.vehicle.stnk_expiry_date)}/>
                  {asset.vehicle.insurance_policy_no && <Row label="Asuransi" v={`${asset.vehicle.insurance_policy_no} · exp ${asset.vehicle.insurance_expiry_date || "-"}`}/>}
                </>
              )}
              {asset.category === "machine" && asset.machine && (
                <>
                  <div className="mt-2 pt-2 border-t border-slate-100 overline text-[10px] text-slate-500">Mesin</div>
                  <Row label="Model/Serial" v={asset.machine.model_serial}/>
                  <Row label="Kapasitas" v={asset.machine.capacity_spec}/>
                  <Row label="Jadwal Servis" v={asset.machine.service_schedule}/>
                  <Row label="Jadwal Kalibrasi" v={asset.machine.calibration_schedule}/>
                </>
              )}
              {asset.description && <div className="mt-2 text-slate-600 whitespace-pre-wrap">{asset.description}</div>}
              {asset.custom_notes && <div className="mt-2 text-slate-600 whitespace-pre-wrap border-t border-slate-100 pt-2">{asset.custom_notes}</div>}
              <div className="mt-3 flex items-center gap-2">
                {canUpdate && <button type="button" data-testid="asset-edit-btn" onClick={onEdit} className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 text-xs hover:bg-slate-50"><Pencil size={11}/> Ubah</button>}
                <button type="button" data-testid="asset-card-btn" onClick={() => printAssetCard(asset)} className="inline-flex items-center gap-1 px-2 py-1 border border-[#0B1D3A] text-[#0B1D3A] text-xs hover:bg-slate-50"><Printer size={11}/> Cetak Asset Card</button>
                {canArchive && asset.status !== "archived" && <button type="button" data-testid="asset-archive-btn" onClick={() => setConfirmArchive(true)} className="inline-flex items-center gap-1 px-2 py-1 border border-amber-400 text-amber-700 text-xs hover:bg-amber-50"><ArchiveIcon size={11}/> Arsipkan</button>}
              </div>
            </div>
          )}
          {tab === "photos" && (
            <div data-testid="tab-photos" className="space-y-3">
              {canUpdate && <PhotoUploader assetId={asset.id} value={asset.photo_url} onChange={onChanged} inline />}
              {asset.photo_url ? <img src={authFileUrl(asset.photo_url)} alt={asset.name} className="max-w-full max-h-96 border border-slate-200"/> : <EmptyTab msg="Belum ada foto"/>}
            </div>
          )}
          {tab === "timeline" && (
            <TimelineTab asset={asset} />
          )}
          {tab === "movements" && (
            <MovementsTab asset={asset} users={users} locations={locations} canUpdate={canUpdate} onChanged={onChanged} />
          )}
          {tab === "inspections" && (
            <InspectionsTab asset={asset} users={users} locations={locations} canUpdate={canUpdate} onChanged={onChanged} />
          )}
          {tab === "services" && (
            <ServicesTab asset={asset} users={users} canUpdate={canUpdate} onChanged={onChanged} />
          )}
          {tab === "documents" && (
            <div data-testid="tab-documents" className="space-y-2">
              {canUpdate && (
                <DocumentUploader assetId={asset.id} onUploaded={onChanged} />
              )}
              {(!asset.documents || asset.documents.length === 0) ? <EmptyTab msg="Belum ada dokumen"/> :
                <ul className="space-y-1">{asset.documents.map((d, i) => (
                  <li key={d.url || `${d.name}-${i}`} className="text-xs flex items-center gap-2 p-1.5 border border-slate-200 hover:bg-slate-50">
                    <FileText size={12} className="text-slate-500"/>
                    <a href={authFileUrl(d.url)} target="_blank" rel="noreferrer" className="text-sky-700 hover:underline flex-1 truncate">{d.name || d.url}</a>
                    <span className="text-[10px] text-slate-400 tabular-nums">{d.size ? `${(d.size/1024).toFixed(1)}KB` : ""}</span>
                    {canUpdate && (
                      <button type="button" data-testid={`doc-delete-${i}`}
                        onClick={async () => { try { await api.delete(`/assets/${asset.id}/documents/${i}`); toast.success("Dihapus"); onChanged(); } catch(e) { toast.error(formatDetail(e.response?.data?.detail)); } }}
                        className="p-0.5 text-rose-600 hover:bg-rose-50"><Trash2 size={11}/></button>
                    )}
                  </li>
                ))}</ul>}
            </div>
          )}
          {tab === "qrcode" && (
            <div data-testid="tab-qrcode" className="text-center py-4">
              <QRCodePanel asset={asset} />
            </div>
          )}
          {tab === "audit" && (
            <div data-testid="tab-audit" className="space-y-1">
              {audit.length === 0 ? <EmptyTab msg="Belum ada audit log"/> :
                audit.map((a) => (
                  <div key={a.id} className="text-xs border-l-2 border-slate-200 pl-2 py-1">
                    <span className="font-semibold uppercase">{a.action}</span>
                    <span className="text-slate-400 mx-1">·</span>
                    <span className="text-slate-600">{a.actor_name}</span>
                    <span className="text-slate-400 mx-1">·</span>
                    <span className="text-[10px] text-slate-500">{fmtDate(a.created_at)}</span>
                  </div>
                ))}
            </div>
          )}
        </div>
        <ConfirmDialog open={confirmArchive} onOpenChange={setConfirmArchive} onConfirm={doArchive}
          title="Arsipkan Asset?" description="Asset ini akan diarsipkan (bukan dihapus). Semua histori tetap tersimpan." confirmLabel="Arsipkan" danger={false}
          confirmClass="bg-amber-500 hover:bg-amber-600 text-white"/>
      </DialogContent>
    </Dialog>
  );
}

const Row = ({ label, v }) => (
  <div className="flex text-xs">
    <span className="w-32 text-slate-500 overline text-[10px]">{label}</span>
    <span className="flex-1 text-slate-800">{v || <span className="text-slate-300">—</span>}</span>
  </div>
);
const EmptyTab = ({ msg }) => (
  <div className="text-center py-8 text-slate-400 text-sm" data-testid="empty-tab">{msg}</div>
);

// 149-az: Compose file URL for <img src>. 2026-10-21: auth via cookie HttpOnly
// ens_token — <img> mengirim cookie otomatis (same-origin); query ?auth= dari
// localStorage sudah tidak dipakai (token dihapus saat migrasi cookie).
const authFileUrl = (path) => {
  if (!path) return "";
  const base = process.env.REACT_APP_BACKEND_URL || "";
  return `${base}${path.startsWith("/") ? path : "/" + path}`;
};

// ---------------------------------------------------------------------------
// 149-az: PhotoUploader — uses authFileUrl for preview
// ---------------------------------------------------------------------------
function PhotoUploader({ assetId, value, onChange, disabled, inline }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !assetId) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/assets/${assetId}/upload?kind=photo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(data.remote ? "Foto berhasil diupload ke cloud" : "Foto berhasil diupload (lokal)");
      if (typeof onChange === "function") onChange(data.url);
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Upload gagal"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  const url = authFileUrl(value);
  return (
    <div className="mt-1 space-y-2">
      {url && !inline && <img src={url} alt="preview" className="max-h-32 border border-slate-200" />}
      <div className="flex items-center gap-2">
        <input ref={inputRef} type="file" accept="image/*" onChange={onFile} disabled={disabled || busy} data-testid="photo-upload-input" className="text-xs" />
        {busy && <span className="text-[10px] text-slate-500">Mengupload…</span>}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 149-ay: DocumentUploader — uploads to documents[] array
// ---------------------------------------------------------------------------
function DocumentUploader({ assetId, onUploaded }) {
  const inputRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/assets/${assetId}/upload?kind=document`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Dokumen berhasil diupload");
      onUploaded && onUploaded();
    } catch (err) { toast.error(formatDetail(err.response?.data?.detail) || "Upload gagal"); }
    finally { setBusy(false); if (inputRef.current) inputRef.current.value = ""; }
  };
  return (
    <div className="border border-slate-200 p-2 flex items-center gap-2">
      <Upload size={12} className="text-slate-500" />
      <input ref={inputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.txt,image/*" onChange={onFile} disabled={busy} data-testid="doc-upload-input" className="text-xs flex-1" />
      {busy && <span className="text-[10px] text-slate-500">Mengupload…</span>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 149-ay: QRCodePanel — printable QR code linking to asset detail
// ---------------------------------------------------------------------------
function QRCodePanel({ asset }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const qrValue = `${origin}/assets?open=${asset.id}`;
  const printQR = () => {
    const w = window.open("", "_blank", "width=400,height=500");
    if (!w) { toast.error("Popup diblokir browser"); return; }
    const svg = document.getElementById(`qr-svg-${asset.id}`)?.outerHTML || "";
    w.document.write(`<!doctype html><html><head><title>QR Asset - ${asset.asset_number}</title>
      <style>body{font-family:sans-serif;text-align:center;padding:20px}h2{margin:8px 0}p{color:#555;margin:4px 0;font-size:12px}svg{width:280px;height:280px}</style>
      </head><body>
      <h2>${asset.name}</h2>
      <p><b>${asset.asset_number}</b></p>
      ${svg}
      <p>${qrValue}</p>
      <p style="font-size:9px;color:#999">PT. Eka Nusa Sinergi · Asset Management</p>
      <script>window.print();setTimeout(()=>window.close(),200)</script>
      </body></html>`);
    w.document.close();
  };
  return (
    <div className="flex flex-col items-center gap-3" data-testid="qrcode-panel">
      <div className="p-4 bg-white border-2 border-[#0B1D3A]">
        <QRCodeSVG
          id={`qr-svg-${asset.id}`}
          value={qrValue}
          size={220}
          level="M"
          bgColor="#ffffff"
          fgColor="#0B1D3A"
          includeMargin={false}
        />
      </div>
      <div className="text-center">
        <div className="text-lg font-display text-[#0B1D3A]">{asset.name}</div>
        <div className="text-xs text-slate-500 tabular-nums">{asset.asset_number}</div>
        <div className="text-[10px] text-slate-400 mt-1 break-all">{qrValue}</div>
      </div>
      <button type="button" data-testid="qrcode-print" onClick={printQR}
        className="inline-flex items-center gap-1.5 px-4 py-2 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#e04a13]">
        <Printer size={12}/> Cetak Label
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 149-ba: MovementsTab — extended movement lifecycle (planned/in_transit/received/returned/cancelled)
// ---------------------------------------------------------------------------
const EMPTY_MOVEMENT = {
  to_location_id: "", to_pic_id: "", to_pic_name_manual: "",
  project_id: "", reason: "", condition_before: "", condition_on_receipt: "",
  return_planned: false, return_date: "",
  handover_by_id: "", handover_by_name_manual: "",
  receiver_id: "", receiver_name_manual: "",
  note: "", moved_at: "", status: "received",
};

function MovementsTab({ asset, users, locations, canUpdate, onChanged }) {
  const [movements, setMovements] = useState([]);
  const [form, setForm] = useState(EMPTY_MOVEMENT);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editReason, setEditReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirmAction, setConfirmAction] = useState(null); // { mid, kind, extra? }

  const load = useCallback(() => {
    if (!asset) return;
    api.get(`/assets/${asset.id}/movements`).then(({data}) => setMovements(data)).catch(() => {});
  }, [asset]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));

  const submit = async () => {
    if (!form.to_location_id) { toast.error("Lokasi tujuan wajib"); return; }
    setBusy(true);
    try {
      // 149-db: resolve `proj:<pid>` → real asset_location.id sebelum submit.
      const resolvedTo = await resolveLocationId(form.to_location_id);
      const payload = { ...form, to_location_id: resolvedTo };
      // Strip empty strings so backend doesn't try resolving
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      if (editingId) {
        if (!editReason.trim()) { toast.error("Alasan koreksi wajib"); setBusy(false); return; }
        await api.patch(`/assets/${asset.id}/movements/${editingId}`, { ...payload, edit_reason: editReason.trim() });
        toast.success("Perpindahan diperbarui");
      } else {
        await api.post(`/assets/${asset.id}/movements`, payload);
        toast.success("Perpindahan tercatat");
      }
      setForm(EMPTY_MOVEMENT); setEditingId(null); setEditReason(""); setShowForm(false);
      load(); onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const startEdit = (m) => {
    setEditingId(m.id);
    setForm({
      to_location_id: m.to_location_id || "", to_pic_id: m.to_pic_id || "", to_pic_name_manual: m.to_pic_name_manual || "",
      project_id: m.project_id || "", reason: m.reason || "",
      condition_before: m.condition_before || "", condition_on_receipt: m.condition_on_receipt || "",
      return_planned: !!m.return_planned, return_date: (m.return_date || "").slice(0, 10),
      handover_by_id: m.handover_by_id || "", handover_by_name_manual: m.handover_by_name_manual || "",
      receiver_id: m.receiver_id || "", receiver_name_manual: m.receiver_name_manual || "",
      note: m.note || "", moved_at: (m.moved_at || "").slice(0, 10), status: m.status || "received",
    });
    setEditReason("");
    setShowForm(true);
  };

  const cancelEdit = () => { setEditingId(null); setEditReason(""); setForm(EMPTY_MOVEMENT); setShowForm(false); };

  const doAction = async () => {
    if (!confirmAction) return;
    const { mid, kind, extra } = confirmAction;
    try {
      if (kind === "confirm") await api.post(`/assets/${asset.id}/movements/${mid}/confirm`);
      else if (kind === "cancel") await api.post(`/assets/${asset.id}/movements/${mid}/cancel`, { reason: extra || "" });
      else if (kind === "return") await api.post(`/assets/${asset.id}/movements/${mid}/return`, { condition_on_return: extra?.condition || "", return_note: extra?.note || "" });
      toast.success("Tersimpan"); setConfirmAction(null); load(); onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  const today = new Date().toISOString().slice(0, 10);

  return (
    <div data-testid="tab-movements" className="space-y-3">
      {canUpdate && !showForm && (
        <button type="button" data-testid="movement-open-form" onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#e04a13]">
          <Plus size={11}/> Catat Perpindahan Baru
        </button>
      )}
      {showForm && (
        <div className="border border-slate-200 p-3 space-y-2 bg-slate-50/50" data-testid="movement-form">
          <div className="flex items-center justify-between">
            <div className="overline text-[10px] text-slate-500">{editingId ? "Koreksi Perpindahan" : "Perpindahan Baru"}</div>
            <button type="button" onClick={cancelEdit} className="text-[10px] text-slate-500 hover:text-rose-600 uppercase">Tutup</button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Tanggal & Jam</label>
              <input type="datetime-local" data-testid="movement-moved-at" value={form.moved_at || ""} onChange={(e) => set("moved_at", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white"/></div>
            <div><label className="overline text-[9px] text-slate-500">Status *</label>
              <select data-testid="movement-status" value={form.status} onChange={(e) => set("status", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                {MOVEMENT_STATUSES.filter((s) => editingId || !["returned","cancelled"].includes(s.v)).map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Lokasi Tujuan *</label>
              <LocationSelect testid="movement-to-location" value={form.to_location_id} onChange={(v) => set("to_location_id", v)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white" required /></div>
            <div><label className="overline text-[9px] text-slate-500">Alasan</label>
              <select data-testid="movement-reason" value={form.reason} onChange={(e) => set("reason", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="">— pilih —</option>
                {MOVEMENT_REASONS.map((r) => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">PIC Baru (opsional)</label>
              <select data-testid="movement-to-pic" value={form.to_pic_id} onChange={(e) => set("to_pic_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="">— pilih dari user —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input placeholder="atau ketik nama manual" data-testid="movement-to-pic-manual" value={form.to_pic_name_manual} onChange={(e) => set("to_pic_name_manual", e.target.value)} className="w-full mt-1 px-2 py-1 border border-slate-300 text-[11px]"/></div>
            <div><label className="overline text-[9px] text-slate-500">Penerima (yang menerima serah-terima)</label>
              <select data-testid="movement-receiver" value={form.receiver_id} onChange={(e) => set("receiver_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="">— pilih user —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select>
              <input placeholder="atau nama manual (non-user)" data-testid="movement-receiver-manual" value={form.receiver_name_manual} onChange={(e) => set("receiver_name_manual", e.target.value)} className="w-full mt-1 px-2 py-1 border border-slate-300 text-[11px]"/></div>
          </div>
          <div><label className="overline text-[9px] text-slate-500">Penyerah (handover by)</label>
            <select data-testid="movement-handover" value={form.handover_by_id} onChange={(e) => set("handover_by_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
              <option value="">— pilih user —</option>
              {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
            <input placeholder="atau nama manual" data-testid="movement-handover-manual" value={form.handover_by_name_manual} onChange={(e) => set("handover_by_name_manual", e.target.value)} className="w-full mt-1 px-2 py-1 border border-slate-300 text-[11px]"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Kondisi Sebelum</label>
              <textarea data-testid="movement-condition-before" value={form.condition_before} onChange={(e) => set("condition_before", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
            <div><label className="overline text-[9px] text-slate-500">Kondisi Saat Diterima</label>
              <textarea data-testid="movement-condition-onreceipt" value={form.condition_on_receipt} onChange={(e) => set("condition_on_receipt", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          </div>
          <div className="grid grid-cols-2 gap-2 items-end">
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-700 mt-3">
              <input type="checkbox" data-testid="movement-return-planned" checked={form.return_planned} onChange={(e) => set("return_planned", e.target.checked)}/>
              Ada rencana pengembalian
            </label>
            {form.return_planned && (
              <div><label className="overline text-[9px] text-slate-500">Tanggal Rencana Pengembalian</label>
                <input type="date" data-testid="movement-return-date" min={today} value={form.return_date} onChange={(e) => set("return_date", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white"/></div>
            )}
          </div>
          <div><label className="overline text-[9px] text-slate-500">Catatan</label>
            <textarea data-testid="movement-note" value={form.note} onChange={(e) => set("note", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          {editingId && (
            <div className="border border-amber-300 bg-amber-50 p-2">
              <label className="overline text-[9px] text-amber-800">Alasan Koreksi * (before/after tersimpan)</label>
              <input data-testid="movement-edit-reason" value={editReason} onChange={(e) => setEditReason(e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-amber-300 text-xs bg-white" placeholder="Mis. salah pilih lokasi, koreksi tanggal"/>
            </div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={cancelEdit} className="px-3 py-1.5 border border-slate-300 text-xs uppercase text-slate-700 hover:bg-slate-50">Batal</button>
            <button type="button" data-testid="movement-submit" disabled={busy} onClick={submit} className="px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#e04a13]">
              {busy ? "Menyimpan…" : editingId ? "Simpan Koreksi" : "Catat"}
            </button>
          </div>
        </div>
      )}
      {movements.length === 0 ? <EmptyTab msg="Belum ada perpindahan"/> :
        <div className="space-y-2">
          {movements.map((m) => <MovementRow key={m.id} m={m} canUpdate={canUpdate} onEdit={startEdit} onConfirm={(mid) => setConfirmAction({mid, kind:"confirm"})} onCancel={(mid, reason) => setConfirmAction({mid, kind:"cancel", extra: reason})} onReturn={(mid, ret) => setConfirmAction({mid, kind:"return", extra: ret})} />)}
        </div>}
      <ConfirmDialog open={!!confirmAction} onOpenChange={(v) => !v && setConfirmAction(null)} onConfirm={doAction}
        title={confirmAction?.kind === "confirm" ? "Konfirmasi Terima?" : confirmAction?.kind === "cancel" ? "Batalkan Perpindahan?" : "Tandai Dikembalikan?"}
        description={confirmAction?.kind === "confirm" ? "Lokasi & PIC asset akan diperbarui ke tujuan movement." : confirmAction?.kind === "cancel" ? "Movement akan ditandai dibatalkan." : "Asset akan kembali ke lokasi asal."}
        confirmLabel={confirmAction?.kind === "confirm" ? "Konfirmasi" : confirmAction?.kind === "cancel" ? "Batalkan" : "Kembalikan"}
        danger={confirmAction?.kind === "cancel"}/>
    </div>
  );
}

function MovementRow({ m, canUpdate, onEdit, onConfirm, onCancel, onReturn }) {
  const [expanded, setExpanded] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [showCancel, setShowCancel] = useState(false);
  const [returnCondition, setReturnCondition] = useState("");
  const [showReturn, setShowReturn] = useState(false);
  const status = MOVEMENT_STATUSES.find((s) => s.v === m.status) || MOVEMENT_STATUSES[0];
  const isOverdue = m.return_planned && m.return_date && m.status === "received" && new Date(m.return_date) < new Date();
  const canConfirm = ["planned", "in_transit"].includes(m.status);
  const canReturn = m.status === "received";
  const canCancel = !["returned", "cancelled"].includes(m.status);
  const canEditFlag = m.status !== "cancelled";
  return (
    <div className={`border p-2.5 text-xs ${isOverdue ? "border-rose-400 bg-rose-50/40" : "border-slate-200"}`} data-testid={`movement-row-${m.id}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider border ${status.cls}`}>{status.label}</span>
            {isOverdue && <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase border border-rose-400 bg-rose-100 text-rose-800">Overdue Return</span>}
            {m.reason && <span className="text-[10px] text-slate-500">· {MOVEMENT_REASONS.find((r) => r.v === m.reason)?.label || m.reason}</span>}
          </div>
          <div className="mt-1 font-semibold text-[#0B1D3A]">
            {m.from_location_name || "—"} <span className="text-slate-400 mx-1">→</span> {m.to_location_name}
          </div>
          <div className="text-[10px] text-slate-500 tabular-nums">
            {m.moved_at && fmtDate(m.moved_at)} · dicatat oleh {m.recorder_name}
            {m.return_date && <> · rencana kembali <span className={isOverdue ? "text-rose-600 font-semibold" : "text-slate-700"}>{fmtDate(m.return_date)}</span></>}
          </div>
          {m.note && <div className="text-slate-600 mt-1">{m.note}</div>}
        </div>
        <div className="flex flex-col gap-1 items-end shrink-0">
          {canUpdate && canConfirm && <button type="button" data-testid={`movement-confirm-${m.id}`} onClick={() => onConfirm(m.id)} className="px-2 py-0.5 border border-emerald-400 bg-emerald-50 text-emerald-700 text-[10px] uppercase font-semibold hover:bg-emerald-100 inline-flex items-center gap-1"><Check size={10}/> Konfirmasi</button>}
          {canUpdate && canReturn && <button type="button" data-testid={`movement-return-${m.id}`} onClick={() => setShowReturn(!showReturn)} className="px-2 py-0.5 border border-indigo-400 bg-indigo-50 text-indigo-700 text-[10px] uppercase font-semibold hover:bg-indigo-100">Kembalikan</button>}
          {canUpdate && canEditFlag && <button type="button" data-testid={`movement-edit-${m.id}`} onClick={() => onEdit(m)} className="px-2 py-0.5 border border-amber-400 bg-amber-50 text-amber-700 text-[10px] uppercase font-semibold hover:bg-amber-100 inline-flex items-center gap-1"><Pencil size={10}/> Koreksi</button>}
          {canUpdate && canCancel && <button type="button" data-testid={`movement-cancel-${m.id}`} onClick={() => setShowCancel(!showCancel)} className="px-2 py-0.5 border border-rose-400 bg-rose-50 text-rose-700 text-[10px] uppercase font-semibold hover:bg-rose-100 inline-flex items-center gap-1"><X size={10}/> Batalkan</button>}
          <button type="button" onClick={() => setExpanded(!expanded)} className="text-[10px] text-slate-500 hover:text-[#0B1D3A]">{expanded ? "Ringkas ▲" : "Detail ▼"}</button>
        </div>
      </div>
      {showCancel && (
        <div className="mt-2 border-t border-slate-200 pt-2 flex items-center gap-1.5">
          <input value={cancelReason} onChange={(e) => setCancelReason(e.target.value)} placeholder="Alasan pembatalan" className="flex-1 px-2 py-1 border border-rose-300 text-[11px]"/>
          <button type="button" data-testid={`movement-cancel-submit-${m.id}`} onClick={() => { onCancel(m.id, cancelReason); setShowCancel(false); }} className="px-2 py-1 bg-rose-600 text-white text-[10px] uppercase font-semibold hover:bg-rose-700">Batalkan</button>
        </div>
      )}
      {showReturn && (
        <div className="mt-2 border-t border-slate-200 pt-2 flex items-center gap-1.5">
          <input value={returnCondition} onChange={(e) => setReturnCondition(e.target.value)} placeholder="Kondisi saat dikembalikan" className="flex-1 px-2 py-1 border border-indigo-300 text-[11px]"/>
          <button type="button" data-testid={`movement-return-submit-${m.id}`} onClick={() => { onReturn(m.id, { condition: returnCondition, note: "" }); setShowReturn(false); }} className="px-2 py-1 bg-indigo-600 text-white text-[10px] uppercase font-semibold hover:bg-indigo-700">Kembalikan</button>
        </div>
      )}
      {expanded && (
        <div className="mt-2 border-t border-slate-200 pt-2 grid grid-cols-2 gap-2 text-[11px]">
          {m.project_name && <div><span className="text-slate-500">Project:</span> {m.project_name}</div>}
          {m.from_pic_name && <div><span className="text-slate-500">PIC lama:</span> {m.from_pic_name}</div>}
          {m.to_pic_name && <div><span className="text-slate-500">PIC baru:</span> {m.to_pic_name}</div>}
          {m.handover_by_name && <div><span className="text-slate-500">Penyerah:</span> {m.handover_by_name}</div>}
          {m.receiver_name && <div><span className="text-slate-500">Penerima:</span> {m.receiver_name}</div>}
          {m.condition_before && <div className="col-span-2"><span className="text-slate-500">Kondisi sebelum:</span> {m.condition_before}</div>}
          {m.condition_on_receipt && <div className="col-span-2"><span className="text-slate-500">Kondisi saat diterima:</span> {m.condition_on_receipt}</div>}
          {m.condition_on_return && <div className="col-span-2"><span className="text-slate-500">Kondisi saat dikembalikan:</span> {m.condition_on_return}</div>}
          {m.cancel_reason && <div className="col-span-2 text-rose-700"><span className="text-slate-500">Alasan batal:</span> {m.cancel_reason}</div>}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 149-ba: TimelineTab — unified feed of asset history events
// ---------------------------------------------------------------------------
function TimelineTab({ asset }) {
  const [events, setEvents] = useState([]);
  const [filterTypes, setFilterTypes] = useState([]);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [loading, setLoading] = useState(false);

  const load = useCallback(() => {
    if (!asset) return;
    setLoading(true);
    const params = {};
    if (filterTypes.length > 0) params.types = filterTypes.join(",");
    if (dateFrom) params.date_from = dateFrom;
    if (dateTo) params.date_to = dateTo;
    api.get(`/assets/${asset.id}/timeline`, { params }).then(({data}) => setEvents(data))
      .catch(() => {}).finally(() => setLoading(false));
  }, [asset, filterTypes, dateFrom, dateTo]);
  useEffect(() => { load(); }, [load]);

  const toggleType = (k) => setFilterTypes((cur) => cur.includes(k) ? cur.filter((x) => x !== k) : [...cur, k]);
  const clearFilters = () => { setFilterTypes([]); setDateFrom(""); setDateTo(""); };

  const exportCsv = () => {
    if (!events.length) { toast.error("Tidak ada event untuk diekspor"); return; }
    const headers = ["at", "kind", "sub_kind", "title", "actor_name"];
    const rows = events.map((e) => headers.map((h) => JSON.stringify(e[h] ?? "")).join(","));
    const csv = [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `timeline_${asset.asset_number}_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
    toast.success(`Timeline diekspor (${events.length} event)`);
  };
  const printTimeline = () => {
    const w = window.open("", "_blank", "width=900,height=800");
    if (!w) { toast.error("Popup diblokir browser"); return; }
    const rows = events.map((e) => {
      const meta = TIMELINE_KIND_META[e.kind] || {};
      return `<tr>
        <td>${e.at ? new Date(e.at).toLocaleString("id-ID") : "-"}</td>
        <td><b>${meta.label || e.kind}</b></td>
        <td>${(e.title || "").replace(/</g, "&lt;")}</td>
        <td>${(e.actor_name || "-").replace(/</g, "&lt;")}</td>
      </tr>`;
    }).join("");
    w.document.write(`<!doctype html><html><head><title>Timeline — ${asset.name}</title>
      <style>@page{size:A4;margin:12mm}body{font-family:sans-serif}h1{color:#0B1D3A;font-size:14pt}h2{font-size:11pt;color:#555}table{width:100%;border-collapse:collapse;font-size:9pt}th,td{border:1px solid #ddd;padding:5px;text-align:left}th{background:#f5f5f5;color:#0B1D3A;font-size:8pt;text-transform:uppercase}</style>
      </head><body>
      <h1>Riwayat Timeline Asset</h1>
      <h2>${asset.name} · ${asset.asset_number}</h2>
      <p style="font-size:9pt;color:#777">Dicetak: ${new Date().toLocaleString("id-ID")} · ${events.length} event</p>
      <table><thead><tr><th>Waktu</th><th>Kategori</th><th>Detail</th><th>Aktor</th></tr></thead><tbody>${rows}</tbody></table>
      <script>window.print();setTimeout(()=>window.close(),300)</script>
      </body></html>`);
    w.document.close();
  };

  const kindOptions = Object.keys(TIMELINE_KIND_META);

  return (
    <div data-testid="tab-timeline" className="space-y-3">
      <div className="border border-slate-200 p-2 bg-slate-50/60 space-y-2">
        <div className="flex items-center justify-between">
          <div className="overline text-[10px] text-slate-600">Filter Timeline</div>
          <div className="flex items-center gap-1.5">
            {(filterTypes.length > 0 || dateFrom || dateTo) && (
              <button type="button" data-testid="timeline-clear-filter" onClick={clearFilters} className="text-[10px] uppercase text-slate-500 hover:text-rose-600">Reset</button>
            )}
            <button type="button" data-testid="timeline-export-csv" onClick={exportCsv} className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 bg-white text-[10px] uppercase tracking-wider hover:bg-slate-50"><Download size={10}/> CSV</button>
            <button type="button" data-testid="timeline-print" onClick={printTimeline} className="inline-flex items-center gap-1 px-2 py-1 border border-slate-300 bg-white text-[10px] uppercase tracking-wider hover:bg-slate-50"><Printer size={10}/> Cetak</button>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {kindOptions.map((k) => {
            const meta = TIMELINE_KIND_META[k];
            const on = filterTypes.includes(k);
            return (
              <button key={k} type="button" data-testid={`timeline-filter-${k}`} onClick={() => toggleType(k)}
                className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase border ${on ? meta.tone : "bg-white text-slate-500 border-slate-200 hover:border-slate-400"}`}>
                {meta.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-[10px] text-slate-500">Dari</label>
          <input type="date" data-testid="timeline-date-from" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="px-2 py-1 border border-slate-300 text-xs bg-white"/>
          <label className="text-[10px] text-slate-500">Sampai</label>
          <input type="date" data-testid="timeline-date-to" value={dateTo} onChange={(e) => setDateTo(e.target.value)} className="px-2 py-1 border border-slate-300 text-xs bg-white"/>
          <span className="ml-auto text-[10px] text-slate-500 tabular-nums" data-testid="timeline-count">{events.length} event</span>
        </div>
      </div>
      {loading ? <div className="text-center py-6 text-slate-400 text-xs">Memuat…</div> :
       events.length === 0 ? <EmptyTab msg="Tidak ada event di rentang filter"/> :
       <ol className="relative border-l-2 border-slate-200 ml-2 space-y-3">
         {events.map((e, idx) => <TimelineItem key={e.id || `${e.at}-${e.kind}-${idx}`} e={e} idx={idx}/>)}
       </ol>}
    </div>
  );
}

function TimelineItem({ e, idx }) {
  const meta = TIMELINE_KIND_META[e.kind] || { label: e.kind, tone: "bg-slate-100 text-slate-700 border-slate-300", icon: History };
  const Icon = meta.icon;
  return (
    <li className="ml-4 pl-2" data-testid={`timeline-event-${idx}`}>
      <span className="absolute -left-3 mt-1 w-5 h-5 bg-white border border-slate-300 flex items-center justify-center text-slate-600">
        <Icon size={11}/>
      </span>
      <div className="text-xs">
        <div className="flex items-center gap-1.5 flex-wrap">
          <span className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase border ${meta.tone}`}>{meta.label}</span>
          {e.sub_kind && <span className="text-[10px] text-slate-500 uppercase">· {e.sub_kind}</span>}
          <span className="text-[10px] text-slate-400 tabular-nums ml-auto">{e.at ? new Date(e.at).toLocaleString("id-ID") : "-"}</span>
        </div>
        <div className="mt-1 font-semibold text-[#0B1D3A]">{e.title || "-"}</div>
        {e.actor_name && <div className="text-[10px] text-slate-500">oleh {e.actor_name}</div>}
        {e.kind === "movement_edit" && e.detail && (
          <div className="mt-1 border-l-2 border-amber-300 bg-amber-50 pl-2 py-1 text-[10px] space-y-0.5">
            {Object.entries(e.detail.before || {}).map(([k, v]) => (
              <div key={k}><span className="text-slate-500">{k}:</span> <span className="line-through text-rose-700">{JSON.stringify(v)}</span> → <span className="text-emerald-700">{JSON.stringify(e.detail.after?.[k])}</span></div>
            ))}
            {e.detail.reason && <div className="text-amber-900 italic">Alasan: {e.detail.reason}</div>}
          </div>
        )}
      </div>
    </li>
  );
}

// ---------------------------------------------------------------------------
// 149-bb: InspectionsTab — periodic inspection with checklist + 4 photo slots + duplicate warning
// ---------------------------------------------------------------------------
const INSPECT_SLOTS = ["depan", "belakang", "kiri", "kanan"];
const INSPECT_CHECKLIST_BY_CAT = {
  vehicle: [
    { key: "body", label: "Body & cat" },
    { key: "ban", label: "Ban & pelek" },
    { key: "kaca", label: "Kaca & spion" },
    { key: "lampu", label: "Lampu utama/rem/sein" },
    { key: "mesin", label: "Mesin & pelumas" },
    { key: "rem", label: "Rem" },
    { key: "aki", label: "Aki & kelistrikan" },
    { key: "dokumen", label: "STNK/BPKB tersedia" },
  ],
  machine: [
    { key: "bodi", label: "Bodi & rangka" },
    { key: "operasi", label: "Fungsi operasi" },
    { key: "belt", label: "Belt / bearing" },
    { key: "pelumas", label: "Pelumas / oli" },
    { key: "kalibrasi", label: "Kalibrasi terakhir" },
    { key: "safety", label: "Safety guard" },
  ],
  office: [
    { key: "fisik", label: "Kondisi fisik" },
    { key: "fungsi", label: "Fungsi normal" },
    { key: "kelengkapan", label: "Kelengkapan aksesoris" },
  ],
  others: [
    { key: "fisik", label: "Kondisi fisik" },
    { key: "fungsi", label: "Fungsi normal" },
  ],
};

function InspectionsTab({ asset, users, locations, canUpdate, onChanged }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const monthNow = new Date().toISOString().slice(0, 7);
  const [form, setForm] = useState({ period: monthNow, inspected_at: "", inspector_id: "", pic_id: "", actual_location_id: "", condition: "", result: "ok", findings: "", remarks: "", recommendation: "", followup_status: "none", checklist: [] });
  const [busy, setBusy] = useState(false);
  const [dupWarning, setDupWarning] = useState(null);

  const template = INSPECT_CHECKLIST_BY_CAT[asset.category] || INSPECT_CHECKLIST_BY_CAT.others;
  useEffect(() => {
    setForm((f) => ({ ...f, checklist: template.map((t) => ({ ...t, ok: true, note: "" })) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [asset.id]);

  const load = useCallback(() => {
    if (!asset) return;
    api.get(`/assets/${asset.id}/inspections`).then(({ data }) => setItems(data)).catch(() => {});
  }, [asset]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));
  const setChecklist = (idx, k, v) => setForm((cur) => {
    const list = [...cur.checklist];
    list[idx] = { ...list[idx], [k]: v };
    return { ...cur, checklist: list };
  });

  const submit = async () => {
    setBusy(true); setDupWarning(null);
    try {
      // 149-db: resolve `proj:<pid>` untuk actual_location_id bila diisi.
      const payload = { ...form };
      if (payload.actual_location_id) payload.actual_location_id = await resolveLocationId(payload.actual_location_id);
      const { data } = await api.post(`/assets/${asset.id}/inspections`, payload);
      if (data.duplicate_warning) {
        toast.warning(`Sudah ada ${data.duplicate_count} pemeriksaan untuk periode ${form.period}. Tersimpan sebagai record baru.`);
        setDupWarning(data.duplicate_count);
      } else {
        toast.success("Pemeriksaan tercatat");
      }
      setShowForm(false);
      setForm({ ...form, condition: "", findings: "", remarks: "", recommendation: "", followup_status: "none" });
      load(); onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const uploadPhoto = async (iid, slot, file) => {
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/assets/${asset.id}/inspections/${iid}/photos?slot=${slot}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Foto ${slot} tersimpan`);
      load();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };

  return (
    <div data-testid="tab-inspections" className="space-y-3">
      {canUpdate && !showForm && (
        <button type="button" data-testid="inspect-open-form" onClick={() => setShowForm(true)}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#e04a13]">
          <Plus size={11}/> Catat Pemeriksaan Baru
        </button>
      )}
      {showForm && (
        <div className="border border-slate-200 p-3 space-y-2 bg-slate-50/50" data-testid="inspect-form">
          <div className="flex items-center justify-between">
            <div className="overline text-[10px] text-slate-500">Pemeriksaan Berkala</div>
            <button type="button" onClick={() => setShowForm(false)} className="text-[10px] text-slate-500 hover:text-rose-600 uppercase">Tutup</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Periode (YYYY-MM)</label>
              <input type="month" data-testid="inspect-period" value={form.period} onChange={(e) => set("period", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white"/></div>
            <div><label className="overline text-[9px] text-slate-500">Tanggal & Jam</label>
              <input type="datetime-local" data-testid="inspect-datetime" value={form.inspected_at} onChange={(e) => set("inspected_at", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white"/></div>
            <div><label className="overline text-[9px] text-slate-500">Hasil *</label>
              <select data-testid="inspect-result" value={form.result} onChange={(e) => set("result", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="ok">OK</option><option value="attention">Butuh Perhatian</option><option value="fail">Gagal</option>
              </select></div>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Pemeriksa</label>
              <select data-testid="inspect-inspector" value={form.inspector_id} onChange={(e) => set("inspector_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="">— saya sendiri —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select></div>
            <div><label className="overline text-[9px] text-slate-500">PIC</label>
              <select data-testid="inspect-pic" value={form.pic_id} onChange={(e) => set("pic_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="">— PIC saat ini —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select></div>
            <div><label className="overline text-[9px] text-slate-500">Lokasi Aktual</label>
              <LocationSelect testid="inspect-location" value={form.actual_location_id} onChange={(v) => set("actual_location_id", v)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white" /></div>
          </div>
          <div>
            <label className="overline text-[9px] text-slate-500">Checklist ({asset.category})</label>
            <div className="border border-slate-200 divide-y divide-slate-100 mt-1 bg-white">
              {form.checklist.map((c, idx) => (
                <div key={c.key} className="flex items-center gap-2 px-2 py-1.5 text-xs">
                  <label className="inline-flex items-center gap-1 min-w-[160px] font-medium text-[#0B1D3A]"><input type="checkbox" data-testid={`inspect-check-${c.key}`} checked={c.ok} onChange={(e) => setChecklist(idx, "ok", e.target.checked)}/>{c.label}</label>
                  <input placeholder="Catatan (opsional)" value={c.note || ""} onChange={(e) => setChecklist(idx, "note", e.target.value)} className="flex-1 px-2 py-0.5 border border-slate-200 text-[11px]"/>
                </div>
              ))}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Kondisi</label>
              <textarea data-testid="inspect-condition" value={form.condition} onChange={(e) => set("condition", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
            <div><label className="overline text-[9px] text-slate-500">Temuan</label>
              <textarea data-testid="inspect-findings" value={form.findings} onChange={(e) => set("findings", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Remarks</label>
              <input data-testid="inspect-remarks" value={form.remarks} onChange={(e) => set("remarks", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
            <div><label className="overline text-[9px] text-slate-500">Rekomendasi</label>
              <input data-testid="inspect-recommendation" value={form.recommendation} onChange={(e) => set("recommendation", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          </div>
          <div>
            <label className="overline text-[9px] text-slate-500">Status Tindak Lanjut</label>
            <select data-testid="inspect-followup" value={form.followup_status} onChange={(e) => set("followup_status", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white max-w-xs">
              <option value="none">Tidak perlu tindak lanjut</option>
              <option value="scheduled">Terjadwal</option>
              <option value="in_progress">Sedang berjalan</option>
              <option value="closed">Selesai</option>
            </select>
          </div>
          <div className="text-[10px] text-slate-500 italic">Foto (4 slot: depan, belakang, kiri, kanan) di-upload setelah simpan pemeriksaan.</div>
          {dupWarning != null && dupWarning > 0 && (
            <div data-testid="inspect-dup-warning" className="border border-amber-300 bg-amber-50 p-2 text-[11px] text-amber-800">⚠ Sudah ada {dupWarning} pemeriksaan periode ini — tetap disimpan sebagai record baru.</div>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-slate-300 text-xs uppercase text-slate-700 hover:bg-slate-50">Batal</button>
            <button type="button" data-testid="inspect-submit" disabled={busy} onClick={submit} className="px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#e04a13]">
              {busy ? "Menyimpan…" : "Simpan Pemeriksaan"}
            </button>
          </div>
        </div>
      )}
      {items.length === 0 ? <EmptyTab msg="Belum ada pemeriksaan"/> :
        <div className="space-y-2">
          {items.map((i) => <InspectionRow key={i.id} item={i} canUpdate={canUpdate} onUploadPhoto={uploadPhoto}/>)}
        </div>}
    </div>
  );
}

function InspectionRow({ item, canUpdate, onUploadPhoto }) {
  const [expanded, setExpanded] = useState(false);
  const p = item.payload || {};
  const photos = p.photos || [];
  const resultCls = p.result === "ok" ? "bg-emerald-100 text-emerald-800 border-emerald-300" : p.result === "attention" ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-rose-100 text-rose-800 border-rose-300";
  const followupLabel = { none: "-", scheduled: "Terjadwal", in_progress: "Berjalan", closed: "Selesai" }[p.followup_status] || "-";
  return (
    <div className="border border-slate-200 p-2.5 text-xs" data-testid={`inspection-row-${item.id}`}>
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-1.5 flex-wrap">
            <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase border border-slate-300 bg-slate-50 text-slate-700 tabular-nums">Periode {p.period}</span>
            <span className={`px-1.5 py-0.5 text-[10px] font-semibold uppercase border ${resultCls}`}>{p.result}</span>
            {p.followup_status && p.followup_status !== "none" && <span className="text-[10px] text-slate-500">Follow-up: {followupLabel}</span>}
          </div>
          <div className="mt-1 text-[11px] text-slate-600">
            {p.inspected_at && fmtDate(p.inspected_at)} · Pemeriksa: <span className="font-semibold">{p.inspector_name || item.created_by_name}</span>
            {p.pic_name && <> · PIC: {p.pic_name}</>}
            {p.actual_location_name && <> · Lokasi: {p.actual_location_name}</>}
          </div>
          {p.condition && <div className="text-slate-700 mt-1">{p.condition}</div>}
        </div>
        <button type="button" onClick={() => setExpanded(!expanded)} className="text-[10px] uppercase text-slate-500 hover:text-[#0B1D3A]">{expanded ? "Ringkas" : "Detail"}</button>
      </div>
      {expanded && (
        <div className="mt-2 border-t border-slate-200 pt-2 space-y-2">
          {p.checklist?.length > 0 && (
            <div>
              <div className="overline text-[9px] text-slate-500 mb-1">Checklist</div>
              <ul className="text-[11px] grid grid-cols-2 gap-x-3 gap-y-0.5">
                {p.checklist.map((c) => (
                  <li key={c.key} className="flex items-center gap-1"><span className={c.ok ? "text-emerald-600" : "text-rose-600"}>{c.ok ? "✓" : "✗"}</span> {c.label} {c.note && <span className="text-slate-500 italic">— {c.note}</span>}</li>
                ))}
              </ul>
            </div>
          )}
          {p.findings && <div><span className="overline text-[9px] text-slate-500 mr-1">Temuan:</span> {p.findings}</div>}
          {p.remarks && <div><span className="overline text-[9px] text-slate-500 mr-1">Remarks:</span> {p.remarks}</div>}
          {p.recommendation && <div><span className="overline text-[9px] text-slate-500 mr-1">Rekomendasi:</span> {p.recommendation}</div>}
          <div>
            <div className="overline text-[9px] text-slate-500 mb-1">Foto Pemeriksaan (4 slot standar)</div>
            <div className="grid grid-cols-4 gap-2">
              {INSPECT_SLOTS.map((slot) => {
                const inSlot = photos.filter((ph) => ph.slot === slot);
                const latest = inSlot[inSlot.length - 1];
                return (
                  <div key={slot} className="border border-slate-200 p-1.5 text-center" data-testid={`inspect-photo-slot-${item.id}-${slot}`}>
                    <div className="overline text-[9px] text-slate-500 mb-1">{slot}</div>
                    {latest ? <img src={authFileUrl(latest.url)} alt={slot} className="w-full h-24 object-cover border border-slate-200"/> : <div className="h-24 flex items-center justify-center text-slate-300 text-[10px] bg-slate-50">—</div>}
                    <div className="text-[9px] text-slate-500 tabular-nums mt-1">{inSlot.length} foto{inSlot.length > 1 ? " (histori)" : ""}</div>
                    {canUpdate && (
                      <label className="mt-1 block cursor-pointer text-[10px] text-[#FF5A1F] hover:underline">
                        + Upload
                        <input type="file" accept="image/*" onChange={(e) => { const f = e.target.files?.[0]; if (f) onUploadPhoto(item.id, slot, f); e.target.value = ""; }} className="hidden" data-testid={`inspect-upload-${item.id}-${slot}`}/>
                      </label>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 149-bb: ServicesTab — service records with condition + next service reminder integration
// ---------------------------------------------------------------------------
function ServicesTab({ asset, users, canUpdate, onChanged }) {
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ service_date: "", service_type: "preventive", vendor: "", work_description: "", cost: "", condition_before: "", condition_after: "", pic_id: "", inspector_id: "", next_service_date: "", note: "" });
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    if (!asset) return;
    api.get(`/assets/${asset.id}/services`).then(({ data }) => setItems(data)).catch(() => {});
  }, [asset]);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setForm((cur) => ({ ...cur, [k]: v }));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = { ...form, cost: form.cost ? Number(form.cost) : null };
      Object.keys(payload).forEach((k) => { if (payload[k] === "") delete payload[k]; });
      await api.post(`/assets/${asset.id}/services`, payload);
      toast.success("Servis tercatat");
      setShowForm(false);
      setForm({ service_date: "", service_type: "preventive", vendor: "", work_description: "", cost: "", condition_before: "", condition_after: "", pic_id: "", inspector_id: "", next_service_date: "", note: "" });
      load(); onChanged();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const totalCost = items.reduce((s, i) => s + (Number(i.payload?.cost) || 0), 0);

  return (
    <div data-testid="tab-services" className="space-y-3">
      <div className="flex items-center justify-between">
        {canUpdate && !showForm && (
          <button type="button" data-testid="service-open-form" onClick={() => setShowForm(true)}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#e04a13]">
            <Plus size={11}/> Catat Servis Baru
          </button>
        )}
        {items.length > 0 && (
          <div className="text-[10px] text-slate-500 tabular-nums ml-auto">
            Total {items.length} servis · biaya kumulatif <span className="text-[#0B1D3A] font-semibold">Rp {totalCost.toLocaleString("id-ID")}</span>
            <span className="text-slate-400 italic ml-1">(catatan asset, bukan jurnal akuntansi)</span>
          </div>
        )}
      </div>
      {showForm && (
        <div className="border border-slate-200 p-3 space-y-2 bg-slate-50/50" data-testid="service-form">
          <div className="flex items-center justify-between">
            <div className="overline text-[10px] text-slate-500">Catat Servis</div>
            <button type="button" onClick={() => setShowForm(false)} className="text-[10px] text-slate-500 hover:text-rose-600 uppercase">Tutup</button>
          </div>
          <div className="grid grid-cols-3 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Tanggal Servis</label>
              <input type="date" data-testid="service-date" value={form.service_date} onChange={(e) => set("service_date", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white"/></div>
            <div><label className="overline text-[9px] text-slate-500">Tipe Servis</label>
              <select data-testid="service-type" value={form.service_type} onChange={(e) => set("service_type", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="preventive">Preventive</option><option value="corrective">Corrective</option><option value="calibration">Kalibrasi</option>
              </select></div>
            <div><label className="overline text-[9px] text-slate-500">Biaya (Rp)</label>
              <input type="number" data-testid="service-cost" value={form.cost} onChange={(e) => set("cost", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Vendor / Bengkel</label>
              <input data-testid="service-vendor" value={form.vendor} onChange={(e) => set("vendor", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
            <div><label className="overline text-[9px] text-slate-500">PIC / Pemeriksa</label>
              <select data-testid="service-pic" value={form.pic_id} onChange={(e) => set("pic_id", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white">
                <option value="">— pilih PIC —</option>
                {users.map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
              </select></div>
          </div>
          <div><label className="overline text-[9px] text-slate-500">Pekerjaan Servis</label>
            <textarea data-testid="service-work" value={form.work_description} onChange={(e) => set("work_description", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Kondisi Sebelum</label>
              <textarea data-testid="service-cond-before" value={form.condition_before} onChange={(e) => set("condition_before", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
            <div><label className="overline text-[9px] text-slate-500">Kondisi Sesudah</label>
              <textarea data-testid="service-cond-after" value={form.condition_after} onChange={(e) => set("condition_after", e.target.value)} rows={2} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div><label className="overline text-[9px] text-slate-500">Jadwal Servis Berikutnya (opsional)</label>
              <input type="date" data-testid="service-next" value={form.next_service_date} onChange={(e) => set("next_service_date", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs bg-white"/>
              <div className="text-[9px] text-slate-500 italic mt-1">Otomatis dibuatkan reminder di Reminder Center (1w & 1d sebelum).</div></div>
            <div><label className="overline text-[9px] text-slate-500">Catatan</label>
              <input data-testid="service-note" value={form.note} onChange={(e) => set("note", e.target.value)} className="w-full mt-1 px-2 py-1.5 border border-slate-300 text-xs"/></div>
          </div>
          <div className="text-[10px] text-amber-800 bg-amber-50 border border-amber-300 p-1.5">
            ⚠ Biaya servis di sini hanya <b>catatan asset</b> — TIDAK masuk transaksi akuntansi. Update jurnal via Accurate.
          </div>
          <div className="flex justify-end gap-2 pt-1">
            <button type="button" onClick={() => setShowForm(false)} className="px-3 py-1.5 border border-slate-300 text-xs uppercase text-slate-700 hover:bg-slate-50">Batal</button>
            <button type="button" data-testid="service-submit" disabled={busy} onClick={submit} className="px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider disabled:opacity-40 hover:bg-[#e04a13]">
              {busy ? "Menyimpan…" : "Simpan Servis"}
            </button>
          </div>
        </div>
      )}
      {items.length === 0 ? <EmptyTab msg="Belum ada servis"/> :
        <div className="space-y-2">
          {items.map((s) => {
            const p = s.payload || {};
            return (
              <div key={s.id} className="border border-slate-200 p-2.5 text-xs" data-testid={`service-row-${s.id}`}>
                <div className="flex items-center gap-1.5 flex-wrap">
                  <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase border border-slate-300 bg-slate-50 text-slate-700">{p.service_type}</span>
                  {p.vendor && <span className="font-semibold text-[#0B1D3A]">{p.vendor}</span>}
                  {p.cost != null && <span className="text-emerald-700 font-semibold tabular-nums ml-auto">Rp {Number(p.cost).toLocaleString("id-ID")}</span>}
                </div>
                <div className="text-[11px] text-slate-600 mt-1">
                  {p.service_date && fmtDate(p.service_date)}
                  {p.pic_name && <> · PIC: {p.pic_name}</>}
                  {p.next_service_date && <> · Berikutnya: <span className="text-[#FF5A1F] font-semibold">{fmtDate(p.next_service_date)}</span></>}
                </div>
                {p.work_description && <div className="text-slate-700 mt-1">{p.work_description}</div>}
                {(p.condition_before || p.condition_after) && (
                  <div className="mt-1 grid grid-cols-2 gap-2 text-[11px]">
                    {p.condition_before && <div><span className="text-slate-500">Sebelum:</span> {p.condition_before}</div>}
                    {p.condition_after && <div><span className="text-slate-500">Sesudah:</span> {p.condition_after}</div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>}
    </div>
  );
}

// ---------------------------------------------------------------------------
// 149-bb: printAssetCard — printable summary card
// ---------------------------------------------------------------------------
async function printAssetCard(asset) {
  try {
    const { data } = await api.get(`/assets/${asset.id}/card`);
    const w = window.open("", "_blank", "width=900,height=1100");
    if (!w) { toast.error("Popup diblokir browser"); return; }
    const p = data.photos || [];
    const li = data.latest_inspection || {};
    const svc = data.services_summary || {};
    const cat = CATEGORIES.find((c) => c.v === asset.category)?.label || asset.category;
    const st = STATUSES.find((s) => s.v === asset.status)?.label || asset.status;
    const veh = asset.vehicle || {};
    const mac = asset.machine || {};
    const photosHtml = p.slice(0, 4).map((ph) => {
      const url = authFileUrl(ph.url);
      return `<div class="photo"><img src="${url}" alt=""/><div class="slot">${(ph.slot || "-").toUpperCase()}</div></div>`;
    }).join("") + Array(Math.max(0, 4 - p.length)).fill('<div class="photo empty">—</div>').join("");
    const svcRows = (svc.latest || []).map((s) => `<tr>
      <td>${s.service_date ? new Date(s.service_date).toLocaleDateString("id-ID") : "-"}</td>
      <td>${s.service_type || "-"}</td>
      <td>${(s.vendor || "-").replace(/</g, "&lt;")}</td>
      <td class="num">${s.cost != null ? "Rp " + Number(s.cost).toLocaleString("id-ID") : "-"}</td>
    </tr>`).join("");
    w.document.write(`<!doctype html><html><head><title>Asset Card — ${asset.asset_number}</title>
      <style>@page{size:A4;margin:12mm}body{font-family:sans-serif;color:#0B1D3A;font-size:10pt}
        h1{margin:0;font-size:16pt;color:#0B1D3A;border-bottom:3px solid #FF5A1F;padding-bottom:4mm}
        .sub{color:#666;font-size:9pt;margin-top:2mm}
        .grid{display:grid;grid-template-columns:1fr 1fr;gap:6mm;margin-top:5mm}
        .box{border:1px solid #ddd;padding:3mm}.box h3{margin:0 0 2mm;font-size:10pt;color:#0B1D3A;text-transform:uppercase;letter-spacing:1px;border-bottom:1px solid #eee;padding-bottom:2mm}
        .row{display:flex;font-size:9pt;padding:1mm 0;border-bottom:1px solid #f5f5f5}
        .row label{width:35%;color:#777}
        .photos{display:grid;grid-template-columns:repeat(4,1fr);gap:2mm;margin-top:5mm}
        .photo{border:1px solid #ddd;text-align:center;padding:1mm;height:35mm;display:flex;flex-direction:column;justify-content:space-between}
        .photo img{max-width:100%;max-height:28mm;object-fit:cover;margin:auto}
        .photo.empty{color:#ccc;font-size:20pt;display:flex;align-items:center;justify-content:center}
        .photo .slot{font-size:7pt;color:#0B1D3A;font-weight:bold;text-transform:uppercase}
        table{width:100%;border-collapse:collapse;font-size:9pt;margin-top:3mm}
        th,td{border:1px solid #ddd;padding:2mm;text-align:left}th{background:#f5f5f5;font-size:8pt;text-transform:uppercase;color:#0B1D3A}
        .num{text-align:right;font-variant-numeric:tabular-nums}
        .stamp{margin-top:6mm;font-size:8pt;color:#999;border-top:1px dashed #ccc;padding-top:3mm}
      </style>
      </head><body>
      <h1>Asset Card — ${asset.name}</h1>
      <div class="sub">Nomor: <b>${asset.asset_number}</b> · Kategori: ${cat} · Status: ${st}</div>
      <div class="grid">
        <div class="box">
          <h3>Master Data</h3>
          <div class="row"><label>Brand / Model</label><span>${(asset.brand || "-") + " / " + (asset.type_model || "-")}</span></div>
          <div class="row"><label>Serial</label><span>${asset.serial_number || "-"}</span></div>
          <div class="row"><label>Pembelian</label><span>${asset.purchase_date ? new Date(asset.purchase_date).toLocaleDateString("id-ID") : "-"} · ${asset.purchase_value ? "Rp " + Number(asset.purchase_value).toLocaleString("id-ID") : "-"}</span></div>
          ${asset.category === "vehicle" ? `<div class="row"><label>No. Polisi</label><span>${veh.plate_number || "-"}</span></div>
            <div class="row"><label>STNK / Pajak</label><span>${veh.stnk_expiry_date || "-"} / ${veh.tax_due_date || "-"}</span></div>
            <div class="row"><label>Asuransi</label><span>${veh.insurance_policy_no || "-"} · exp ${veh.insurance_expiry_date || "-"}</span></div>` : ""}
          ${asset.category === "machine" ? `<div class="row"><label>Model/Serial</label><span>${mac.model_serial || "-"}</span></div>
            <div class="row"><label>Kapasitas</label><span>${mac.capacity_spec || "-"}</span></div>
            <div class="row"><label>Servis / Kalibrasi</label><span>${mac.service_schedule || "-"} / ${mac.calibration_schedule || "-"}</span></div>` : ""}
        </div>
        <div class="box">
          <h3>Posisi Terkini</h3>
          <div class="row"><label>Lokasi</label><span><b>${asset.location_name || "-"}</b></span></div>
          <div class="row"><label>PIC</label><span>${asset.pic_name || "-"}</span></div>
          <div class="row"><label>Terakhir Update</label><span>${asset.updated_at ? new Date(asset.updated_at).toLocaleString("id-ID") : (asset.created_at ? new Date(asset.created_at).toLocaleString("id-ID") : "-")}</span></div>
          <h3 style="margin-top:4mm">Pemeriksaan Terakhir</h3>
          ${li.period ? `
            <div class="row"><label>Periode</label><span>${li.period}</span></div>
            <div class="row"><label>Hasil</label><span>${(li.result || "-").toUpperCase()}</span></div>
            <div class="row"><label>Pemeriksa</label><span>${li.inspector_name || "-"}</span></div>
            <div class="row"><label>Kondisi</label><span>${li.condition || "-"}</span></div>
            ${li.recommendation ? `<div class="row"><label>Rekomendasi</label><span>${li.recommendation}</span></div>` : ""}
          ` : `<div style="color:#999;font-style:italic">Belum ada pemeriksaan</div>`}
        </div>
      </div>
      <h3 style="margin-top:6mm">Foto Terbaru (4 slot)</h3>
      <div class="photos">${photosHtml}</div>
      <h3 style="margin-top:6mm">Ringkasan Servis</h3>
      <div class="sub">Total: ${svc.count || 0} servis · biaya kumulatif Rp ${(svc.total_cost || 0).toLocaleString("id-ID")} · berikutnya: ${svc.next_service_date || "-"}</div>
      ${svcRows ? `<table><thead><tr><th>Tanggal</th><th>Tipe</th><th>Vendor</th><th class="num">Biaya</th></tr></thead><tbody>${svcRows}</tbody></table>` : ""}
      <div class="stamp">Dicetak: ${new Date().toLocaleString("id-ID")} · PT. Eka Nusa Sinergi · Asset Management</div>
      <script>window.print();setTimeout(()=>window.close(),400)</script>
      </body></html>`);
    w.document.close();
  } catch (e) {
    toast.error(formatDetail(e.response?.data?.detail) || "Gagal generate Asset Card");
  }
}

// ---------------------------------------------------------------------------
// Location Manager Dialog
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// 149-az: BulkQRDialog — grid of QR codes for selected assets + bulk print
// ---------------------------------------------------------------------------
function BulkQRDialog({ open, onOpenChange, assets, onClear }) {
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const printAll = () => {
    const w = window.open("", "_blank", "width=900,height=800");
    if (!w) { toast.error("Popup diblokir browser"); return; }
    const cards = assets.map((a) => {
      const qr = document.getElementById(`bulk-qr-svg-${a.id}`)?.outerHTML || "";
      const url = `${origin}/assets?open=${a.id}`;
      return `<div class="card">
        <div class="qr">${qr}</div>
        <div class="name">${a.name}</div>
        <div class="num">${a.asset_number}</div>
        <div class="url">${url}</div>
      </div>`;
    }).join("");
    w.document.write(`<!doctype html><html><head><title>QR Labels — ${assets.length} Asset</title>
      <style>
        @page { size: A4; margin: 12mm; }
        body { font-family: sans-serif; margin: 0; padding: 0; }
        .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 8mm; }
        .card { border: 1px solid #0B1D3A; padding: 6mm; text-align: center; break-inside: avoid; page-break-inside: avoid; }
        .qr svg { width: 100%; height: auto; max-width: 45mm; }
        .name { font-size: 11pt; font-weight: 700; color: #0B1D3A; margin-top: 3mm; }
        .num { font-size: 9pt; color: #555; margin-top: 1mm; }
        .url { font-size: 6pt; color: #999; word-break: break-all; margin-top: 2mm; }
        h1 { font-size: 12pt; text-align: center; margin: 4mm 0; }
      </style></head><body>
      <h1>ENS Asset Labels — ${assets.length} Item</h1>
      <div class="grid">${cards}</div>
      <script>window.print(); setTimeout(() => window.close(), 300)</script>
      </body></html>`);
    w.document.close();
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="bulk-qr-dialog">
        <DialogHeader><DialogTitle>Cetak QR Label — {assets.length} Asset</DialogTitle>
        <DialogDescription className="text-xs text-slate-500">Preview QR label untuk semua asset terpilih. Klik "Cetak Semua" untuk print via browser.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          {assets.map((a) => (
            <div key={a.id} className="border border-slate-200 p-3 text-center">
              <div className="flex justify-center">
                <QRCodeSVG id={`bulk-qr-svg-${a.id}`} value={`${origin}/assets?open=${a.id}`} size={120} level="M" fgColor="#0B1D3A"/>
              </div>
              <div className="mt-2 text-xs font-semibold text-[#0B1D3A] truncate">{a.name}</div>
              <div className="text-[10px] text-slate-500 tabular-nums">{a.asset_number}</div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <button type="button" onClick={() => { onOpenChange(false); onClear(); }} className="px-3 py-1.5 border border-slate-300 text-xs uppercase tracking-wider text-slate-700 hover:bg-slate-50">Batal</button>
          <button type="button" data-testid="bulk-qr-print-btn" onClick={printAll} className="px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider font-semibold hover:bg-[#e04a13] inline-flex items-center gap-1.5"><Printer size={12}/> Cetak Semua ({assets.length})</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LocationManagerDialog({ open, onOpenChange, onReload, locations, canManage }) {
  const [editing, setEditing] = useState(null);
  const [f, setF] = useState({ name: "", type: "kantor", address: "", city: "", notes: "", is_active: true });
  useEffect(() => {
    setF(editing ? { name: editing.name || "", type: editing.type || "kantor",
                     address: editing.address || "", city: editing.city || "",
                     notes: editing.notes || "", is_active: editing.is_active !== false }
                 : { name: "", type: "kantor", address: "", city: "", notes: "", is_active: true });
    /* eslint-disable-next-line */
  }, [editing]);
  const save = async () => {
    try {
      if (editing?.id) await api.patch(`/assets/locations/${editing.id}`, f);
      else await api.post("/assets/locations", f);
      toast.success("Lokasi tersimpan"); setEditing(null); onReload();
    } catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  const del = async (l) => {
    try { await api.delete(`/assets/locations/${l.id}`); toast.success("Lokasi dihapus"); onReload(); }
    catch (e) { toast.error(formatDetail(e.response?.data?.detail)); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-xl" data-testid="location-manager-dialog">
        <DialogHeader><DialogTitle>Kelola Lokasi Asset</DialogTitle></DialogHeader>
        <div className="max-h-64 overflow-y-auto border border-slate-200">
          {locations.length === 0 && (
            <div className="text-xs text-slate-500 italic p-3 text-center">
              Belum ada lokasi manual. Isi form di bawah untuk membuat lokasi baru
              (Kantor, Gudang, Vendor, dll). Parent project Aktif/Closing sudah otomatis
              tersedia sebagai pilihan lokasi.
            </div>
          )}
          {locations.map((l) => (
            <div key={l.id} data-testid={`loc-row-${l.id}`} className="flex items-center gap-2 px-2 py-1.5 border-b border-slate-100 last:border-b-0 hover:bg-slate-50">
              <MapPin size={11} className="text-slate-500"/>
              <span className="text-sm flex-1">
                {l.name}
                {l.city && <span className="text-[10px] text-slate-500"> · {l.city}</span>}
                {l.linked_project_id && <span className="text-[10px] text-slate-500"> · (Project)</span>}
              </span>
              <span className="text-[10px] px-1 py-0.5 border border-slate-300 text-slate-600 uppercase tracking-wider">{LOCATION_TYPES.find((t) => t.v === l.type)?.label}</span>
              {!l.is_active && <span className="text-[10px] px-1 py-0.5 bg-slate-100 text-slate-500">Nonaktif</span>}
              {canManage && !l.linked_project_id && <>
                <button type="button" onClick={() => setEditing(l)} data-testid={`loc-edit-${l.id}`} className="p-1 text-slate-600 hover:text-[#FF5A1F]"><Pencil size={11}/></button>
                <button type="button" onClick={() => del(l)} data-testid={`loc-delete-${l.id}`} className="p-1 text-rose-600" title="Nonaktifkan (soft-delete)"><X size={11}/></button>
              </>}
            </div>
          ))}
        </div>
        {canManage && (
          <div className="border-t border-slate-200 pt-3 space-y-2">
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-600">{editing ? "Ubah" : "Tambah"} Lokasi</div>
            <input type="text" placeholder="Nama lokasi *" data-testid="loc-name" value={f.name} onChange={(e) => setF({...f, name: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 text-sm"/>
            <div className="grid grid-cols-2 gap-2">
              <input type="text" placeholder="Kota" data-testid="loc-city" value={f.city} onChange={(e) => setF({...f, city: e.target.value})} className="px-2 py-1.5 border border-slate-300 text-sm"/>
              <select data-testid="loc-type" value={f.type} onChange={(e) => setF({...f, type: e.target.value})} className="px-2 py-1.5 border border-slate-300 text-xs bg-white">
                {LOCATION_TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
              </select>
            </div>
            <input type="text" placeholder="Alamat (opsional)" data-testid="loc-address" value={f.address} onChange={(e) => setF({...f, address: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 text-sm"/>
            <input type="text" placeholder="Catatan" data-testid="loc-notes" value={f.notes} onChange={(e) => setF({...f, notes: e.target.value})} className="w-full px-2 py-1.5 border border-slate-300 text-xs"/>
            <div className="flex items-center gap-3">
              <label className="inline-flex items-center gap-1.5 text-xs">
                <input type="checkbox" checked={f.is_active} onChange={(e) => setF({...f, is_active: e.target.checked})}/> Aktif
              </label>
              {editing && <button type="button" onClick={() => setEditing(null)} className="text-[10px] text-slate-500 underline">Batal</button>}
              <button type="button" data-testid="loc-save" onClick={save} className="ml-auto px-3 py-1.5 bg-[#FF5A1F] text-white text-xs uppercase tracking-wider">Simpan</button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
